import { plTotal as powerLevelOf } from '../shared/array-types';
import { creatureDisplayAccess } from '../janus-engine/display/creature-display-access';
import { refuseUnported } from '../janus-engine/unported';
import { holdPendingEntityCard, type EntityCardPaintHooks } from './entity-card-state';
// Browser creature cards render readable shared blobs with viewer-local gates.
// Pane contents repaint on caster updates; the shell refreshes when loot/peek
// visibility changes. Unsupported contexts return false for a named refusal.

import {
    itemsCol, layerValue, layerOf, layerHas, layerEntriesOf, layerListEntryUuid, scalarLayerSlot,
    computedValue, pictureListOf, itemVideos, encounterCreatureField, encounterField,
    hasPlayerPermission, hasCampaignPermission, inventoryField, sizeIdOf, plPretty, plExact,
} from '../shared/array-types';
import { buildNamesMapFromBlob } from './build-names-map';
import { setOpenSideInfo } from './open-side-info';
import { inventoryTileMount, renderMeshMarker } from './render-object-card';
import { nodeHasStatusItemId, INTEGRATED_OBJECT_ITEM_ID, findFullNestedItem } from './find-cached-item';
import type { CachedItemNode } from './find-cached-item';
// Class-mechanic slider bars (fury / tactics / ether / …) — shared with the PC card. An encounter
// creature can carry a class-mechanic passive WITHOUT being a member of that class (e.g. a Summoner
// transfers Ether to an Elidlon), so the bar renders off the active passive_ability, not class
// membership — exactly what buildClassMechanicBars already does. Function-scoped import: it's only
// invoked at render time, so the creature↔player module cycle resolves lazily (esbuild-safe).
import { buildClassMechanicBars, ftRenderCasterCardFromCache } from './render-player-card';
import type { ReferencedNames } from './types';
import { replaceHydratedHtml } from '../shared/replace-hydrated-html';
import { paintMarkup } from '../player-character-sheet/paint-markup';
// Shared shell twins + catalog UUIDs + verbatim PHP style blocks — extracted to card-shell.ts
// (2026-06-12) so render-player-card.ts reuses them; logic unchanged.
import {
    AB_SPOT_HIDDEN, AB_SEARCH_HIDDEN_INV, AB_RESOLVE, AB_SNAP_OUTTA_IT, AB_STOP_BLEEDING,
    AB_CUSTOM_FULL, AB_CUSTOM, AB_CUSTOM_QUICK, AB_CUSTOM_FREE, AB_CUSTOM_REACTION,
    ST_BLEEDING, ST_MODERATE_BLEEDING, ST_SEVERE_BLEEDING,
    esc, iconPh, ptMount, cookieVal, hexOf, prettyNum, fracOf, bucketScalar, hasStatus,
    triggerIsActive, uuidOk, paneMount, ABILITIES_TAB_STYLE, WEAPONS_TAB_STYLE,
    buildCarousel, sourceFooterHtml,
} from './card-shell';

// ── creature-only catalog constants ──────────────────────────────────────────────────────────────
const SIZE_TINY_DEFAULT = '13456FEC-A2EE-DB46-5829-0EB2F627E1B7';
const INFO_LEGENDARY = 'C79897C5-9A37-4EF9-A33A-16A63C8877A0';
const INFO_CR = '704EC146-6160-43CA-9D78-9B76D73DE035';

type CreatureBlob = CachedItemNode;

type CreatureCardWindow = Record<string, unknown> & {
    _ft_user_id?: unknown;
    ftGetCampaignId?: () => unknown;
    ftGetMasterCasterId?: () => unknown;
    ftGetActiveCasterId?: () => unknown;
    ftMountAbilityViewToolbars?: () => void;
    ftMountCarousels?: (root?: Element | Document) => void;
    ftMountIcons?: (root?: Element | Document) => void;
    ftMountInventoryTiles?: (root?: Element | Document) => void;
    ftMountParsedText?: (root?: Element | Document) => void;
    ftMountParseTextTables?: (root?: Element | Document) => void;
    ftMountPcSheet?: (root?: ParentNode) => void;
    ftMountRuneIcons?: (root?: Element | Document) => void;
    // catalog-list bundle scanner — hydrates [data-ft-catalog-list] mounts. The Family pane is one,
    // and it arrives via innerHTML after the bundle's own scans, so the card re-invokes this.
    ftMountCatalogLists?: () => void;
    ftMountThumbnails?: (root?: Element | Document) => void;
    ftSeedCasterFromEncounterCache?: (casterId: string) => void;
    getSidelogContext?: () => unknown;
    refreshDragAndDrop?: () => void;
    renderCasterSpecialActionsInline?: (casterId: string, targetId: string, sideInfo: boolean) => void;
    renderCreatureHarvestYields?: (blob: CreatureBlob, names: ReferencedNames, target: HTMLElement) => void;
    renderCreatureInventory?: (blob: CreatureBlob, names: ReferencedNames, target: HTMLElement) => void;
    renderCreatureSummonsLists?: (blob: CreatureBlob, names: ReferencedNames, target: HTMLElement) => void;
    renderCreatureLairLists?: (blob: CreatureBlob, names: ReferencedNames, target: HTMLElement) => void;
    renderCreatureMountLists?: (blob: CreatureBlob, names: ReferencedNames, target: HTMLElement) => void;
};

function recordOf(value: unknown): CreatureBlob | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as CreatureBlob
        : null;
}

function creatureWindow(): CreatureCardWindow {
    return window as unknown as CreatureCardWindow;
}

function computedCreature(blob: CreatureBlob): CreatureBlob | null {
    return recordOf(recordOf(blob.computed)?.creature);
}

function nestedItemName(value: unknown): string {
    return String(itemsCol(value, 'item_name') ?? '');
}


function lastUpdatedOf(blob: CreatureBlob): unknown {
    const value = blob.item_arr_last_t_updated;
    return Array.isArray(value) ? value[0] : value;
}

function ensureRecord(parent: CreatureBlob, key: string): CreatureBlob {
    const existing = recordOf(parent[key]);
    if (existing) return existing;
    const created: CreatureBlob = {};
    parent[key] = created;
    return created;
}

function ccActionAttrs(action: string, attrs: Record<string, string> = {}): string {
    const pairs = [`data-ft-creature-card-action="${esc(action)}"`];
    for (const [key, value] of Object.entries(attrs)) {
        // HTML lowercases attribute names, so `data-ft-itemId` reads back as dataset.ftItemid,
        // NOT dataset.ftItemId. Kebab-case the key so the camelCase dataset readers resolve.
        pairs.push(`data-ft-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}="${esc(value)}"`);
    }
    return pairs.join(' ');
}

function ccInfoAttrs(itemId: string): string {
    return ccActionAttrs('open-info', { itemId });
}

function ccPowerAttrs(itemId: string): string {
    return ccActionAttrs('open-power-level', { itemId });
}

function ccCall(name: string, ...args: unknown[]): unknown {
    const fn = creatureWindow()[name];
    return typeof fn === 'function' ? fn(...args) : undefined;
}

interface Ft3dCreatureSheetView {
    activeTab: string;
    scrollByTab: Record<string, number>;
}

const FT3D_CREATURE_SHEET_VIEW_KEY = 'ft3d_creature_sheet_view_v1';
const FT3D_CREATURE_SHEET_VIEW_MAX_CASTERS = 24;
const ft3dCreatureScrollTimers = new WeakMap<HTMLElement, number>();
const ft3dCreatureScrollRestoreUntil = new WeakMap<HTMLElement, number>();

function readFt3dCreatureSheetViews(): Record<string, Ft3dCreatureSheetView> {
    try {
        const raw = localStorage.getItem(FT3D_CREATURE_SHEET_VIEW_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as unknown;
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? parsed as Record<string, Ft3dCreatureSheetView> : {};
    } catch {
        try { localStorage.removeItem(FT3D_CREATURE_SHEET_VIEW_KEY); } catch { /* viewer-local nicety */ }
        return {};
    }
}

function ft3dLiveCreatureCard(element: Element | null): HTMLElement | null {
    const card = element?.closest<HTMLElement>('[data-ft-creature-card-cache="1"][data-ft-caster-id]') ?? null;
    return card?.closest('[data-ft3d-creature-sheet-demo-active="1"]') && !card.dataset.ftStaticCreature
        ? card : null;
}

function ft3dCreatureActiveTab(card: HTMLElement): string {
    return card.querySelector<HTMLElement>('.character-console-nav [data-ft-tab].active')?.dataset.ftTab || '';
}

function writeFt3dCreatureSheetView(casterId: string, state: Ft3dCreatureSheetView): void {
    if (!casterId) return;
    try {
        const store = readFt3dCreatureSheetViews();
        delete store[casterId];
        store[casterId] = {
            activeTab: state.activeTab,
            scrollByTab: Object.fromEntries(Object.entries(state.scrollByTab)
                .filter(([key, value]) => key && Number.isFinite(value))
                .slice(-16)),
        };
        const ids = Object.keys(store);
        for (let index = 0; index < ids.length - FT3D_CREATURE_SHEET_VIEW_MAX_CASTERS; index += 1) {
            const stale = ids[index];
            if (stale) delete store[stale];
        }
        localStorage.setItem(FT3D_CREATURE_SHEET_VIEW_KEY, JSON.stringify(store));
    } catch { /* persistence must never block the sheet */ }
}

function rememberFt3dCreatureTab(button: HTMLElement): void {
    const card = ft3dLiveCreatureCard(button);
    const content = card?.closest<HTMLElement>('.ft3d-campaign-window')
        ?.querySelector<HTMLElement>(':scope > .ft3d-campaign-window__content') ?? null;
    const casterId = card?.dataset.ftCasterId || '';
    const nextTab = button.dataset.ftTab || '';
    if (!card || !content || !casterId || !nextTab) return;
    const previous = readFt3dCreatureSheetViews()[casterId] ?? { activeTab: '', scrollByTab: {} };
    const currentTab = ft3dCreatureActiveTab(card) || previous.activeTab;
    if (currentTab) previous.scrollByTab[currentTab] = Math.max(0, Math.round(content.scrollTop));
    previous.activeTab = nextTab;
    writeFt3dCreatureSheetView(casterId, previous);
    window.setTimeout(() => {
        content.scrollTop = Math.max(0, Number(previous.scrollByTab[nextTab] ?? 0));
    }, 0);
}

function restoreFt3dCreatureSheetView(target: HTMLElement, casterId: string): void {
    const card = ft3dLiveCreatureCard(target.querySelector('[data-ft-creature-card-cache="1"]'));
    const state = readFt3dCreatureSheetViews()[casterId];
    if (!card || !state?.activeTab) return;
    const button = Array.from(card.querySelectorAll<HTMLElement>('.character-console-nav [data-ft-tab]'))
        .find(candidate => candidate.dataset.ftTab === state.activeTab);
    if (!button) return;
    if (!button.classList.contains('active')) button.click();
    const content = card.closest<HTMLElement>('.ft3d-campaign-window')
        ?.querySelector<HTMLElement>(':scope > .ft3d-campaign-window__content') ?? null;
    if (!content) return;
    const desiredScroll = Math.max(0, Number(state.scrollByTab[state.activeTab] ?? 0));
    // Restoring a lazy tab is a two-stage paint: Bootstrap activates the pane, then the tab module
    // fills it. An immediate scroll assignment can therefore clamp to zero against the temporary
    // empty pane. Reapply briefly across that paint window and suppress those synthetic scrolls from
    // replacing the viewer's saved value with zero.
    ft3dCreatureScrollRestoreUntil.set(content, performance.now() + 700);
    const applyDesiredScroll = (): void => { content.scrollTop = desiredScroll; };
    window.requestAnimationFrame(() => window.requestAnimationFrame(applyDesiredScroll));
    window.setTimeout(applyDesiredScroll, 80);
    window.setTimeout(applyDesiredScroll, 220);
    window.setTimeout(applyDesiredScroll, 500);
}

document.addEventListener('scroll', (event) => {
    const content = event.target instanceof HTMLElement
        && event.target.classList.contains('ft3d-campaign-window__content') ? event.target : null;
    const card = content ? ft3dLiveCreatureCard(content.querySelector('[data-ft-creature-card-cache="1"]')) : null;
    const casterId = card?.dataset.ftCasterId || '';
    if (!content || !card || !casterId) return;
    if (performance.now() < (ft3dCreatureScrollRestoreUntil.get(content) ?? 0)) return;
    const priorTimer = ft3dCreatureScrollTimers.get(content);
    if (priorTimer !== undefined) window.clearTimeout(priorTimer);
    ft3dCreatureScrollTimers.set(content, window.setTimeout(() => {
        const state = readFt3dCreatureSheetViews()[casterId] ?? { activeTab: '', scrollByTab: {} };
        const activeTab = ft3dCreatureActiveTab(card) || state.activeTab;
        if (!activeTab) return;
        state.activeTab = activeTab;
        state.scrollByTab[activeTab] = Math.max(0, Math.round(content.scrollTop));
        writeFt3dCreatureSheetView(casterId, state);
    }, 120));
}, true);

document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-creature-card-action]')
        : null;
    if (!el) return;
    const action = el.dataset.ftCreatureCardAction || '';
    if (action === 'open-info') {
        event.preventDefault();
        ccCall('openItemInfo', el.dataset.ftItemId || '');
        return;
    }
    if (action === 'open-size-info') {
        event.preventDefault();
        ccCall('forceOpenSideInfoPanel');
        void ccCall('renderSizeInfoPanel', el.dataset.ftCasterId || '');
        return;
    }
    if (action === 'open-power-level') {
        event.preventDefault();
        ccCall('openPowerLevelInfo', el.dataset.ftItemId || '');
        return;
    }
    if (action === 'single-ability') {
        event.preventDefault();
        const abilityId = el.dataset.ftAbilityId || '';
        const casterId = el.closest<HTMLElement>('[data-ft-caster-id]')?.dataset.ftCasterId || '';
        if (!ccCall('ftDispatchAbilityClick', casterId, abilityId)) {
            // Cast AS this card's creature, not whichever one is active (action-bar.ts ftLegacyAbilityClickAs).
            if (typeof creatureWindow().ftLegacyAbilityClickAs === 'function') ccCall('ftLegacyAbilityClickAs', casterId, abilityId);
            else ccCall('handleAbilityBtnSingleClick', abilityId);
        }
        return;
    }
    if (['pickpocket', 'trade', 'train'].includes(action)) {
        event.preventDefault();
        const targetId = el.dataset.ftTargetId ?? '';
        const viewerId = el.dataset.ftViewerId ?? '';
        void (async () => {
            const w = creatureWindow();
            const bridge = action === 'pickpocket' ? 'ftUseAbilityAtExplicitTarget' : action === 'trade' ? 'enterTradeMode' : 'enterTrainMode';
            if (typeof w[bridge] !== 'function') { refuseUnported(`unported:creature-display:${action}:bridge-not-held`); return; }
            if (action === 'pickpocket') {
                await ccCall('ftReturnToMaster');
                await ccCall(bridge, '35B277D9-F5E7-14A7-D2B1-9326C410549A', targetId, 'peek_in_bags');
            } else ccCall(bridge, targetId, viewerId, el.dataset.ftOtherMode === '1');
        })().catch(error => refuseUnported(`unported:creature-display:${action}:${String(error)}`, undefined, undefined, `unported:creature-display:${action}:request-failed`));
        return;
    }
    if (action === 'trigger-primary') {
        event.preventDefault();
        ccCall('ftTriggeredAbilityDebouncedPrimaryClick', event, el);
        return;
    }
    if (action === 'toggle-trigger') {
        event.preventDefault();
        ccCall(
            'toggleTriggerActive',
            el.dataset.ftAbilityId || '',
            el.dataset.ftEncounterCreatureId || '',
            el.dataset.ftEncounterId || '',
            el.dataset.ftActive === '1' ? 1 : 0,
        );
        return;
    }
    if (action === 'clear-triggers') {
        event.preventDefault();
        ccCall('clearTriggersFromCoreAbilitiesButton', el);
        return;
    }
    if (action === 'add-ap') {
        event.preventDefault();
        ccCall('addAPToCreature');
        return;
    }
    if (action === 'add-reaction') {
        event.preventDefault();
        ccCall('addReactionToCreature');
        return;
    }
    if (action === 'set-tab-cookie') {
        ccCall('setCookie', 'cookie_player_character_sheet_menu_tab', `tab_${el.dataset.ftTab || ''}`, 7);
        if (el.dataset.ftAssertCasterId) ccCall('ftAssertSelfCasterOnTab', el.dataset.ftAssertCasterId);
        rememberFt3dCreatureTab(el);
    }
});

// ── HEADER (port of _creature_header_inc.php, encounter branch) ──────────────────────────────────
function buildHeader(blob: CreatureBlob, ecId: string, names: ReferencedNames, canControl = creatureDisplayAccess(blob).control): string {
    const ec = recordOf(blob.encounter_creatures) ?? {};
    const nameOf = (uuid: string): string => {
        const n = names[String(uuid).toUpperCase()];
        const nm = n && typeof n === 'object' ? (n.item_name ?? n.name) : n;
        return typeof nm === 'string' ? nm : '';
    };

    let h = '<div class="card-title"><div class="d-flex align-items-center">';
    h += '<div class="d-flex align-items-center justify content start flex-grow-1 h5 mb-0 pb-0">';
    h += `<div data-ft-creature-header-name>${esc(bucketScalar(ec, 'encounter_creature_name'))}</div>`;
    if (Number(bucketScalar(ec, 'encounter_creature_deleted'))) {
        h += `<div><span class="ml-2 badge bg-danger">${iconPh('creature')} Deleted</span></div>`;
    }
    if (Number(bucketScalar(ec, 'encounter_creature_private')) || Number(itemsCol(blob, 'item_private') ?? 0)) {
        h += `<div><span class="ml-2 badge bg-secondary">${iconPh('encounter_creature_private')} Private</span></div>`;
    }
    h += '</div>';

    h += '<div><h5>';
    const heroforge = layerOf(blob, 'heroforge_link');
    if (heroforge) {
        h += `<a href="${esc(heroforge)}" target="_blank">${iconPh('heroforge_link')}</a> `;
    }
    const gender = bucketScalar(ec, 'gender');
    if (gender) {
        h += `<span role="button" ${ccInfoAttrs(String(gender))}>${iconPh(gender, 20, 20)}</span> `;
    }
    const creatureNumber = bucketScalar(ec, 'creature_number');
    if (creatureNumber !== '') {
        h += `<span class="badge bg-dark border" data-ft-creature-header-number>${esc(creatureNumber)}</span> `;
    }
    h += `<a href="${BASE_URL}item.php?id=${esc(ecId)}" target="_blank">${iconPh('creature')}</a> `;
    const creatureType = layerOf(blob, 'creature_type');
    if (creatureType && canControl) {
        h += `<a href="${BASE_URL}item.php?id=${esc(itemsCol(blob, 'item_id') ?? '')}">${iconPh(creatureType)}</a>`;
    }
    h += '</h5></div></div>';

    // OBJECT TAGS row
    h += '<div class="d-flex align-items-center">';
    h += '<div class="d-flex align-items-start flex-grow-1 min-w-0" data-ft-creature-classification-list>';
    // sizeIdOf bridges to computed.creature.size.breakdown.final_size_id first (mirrors PHP
    // layer_value('size')) — a bare layerOf read can surface a taxonomy carrier's BASE size
    // now that PC blobs retain the creature_sub_type carrier slot.
    const sizeRaw = sizeIdOf(blob) ?? layerOf(blob, 'size');
    if (sizeRaw) {
        const sizeId = String(sizeRaw);
        const clickId = sizeId || SIZE_TINY_DEFAULT;
        h += `<div class="flex-shrink-0" data-ft-creature-classification-size><span class="cursor-pointer flex-shrink-0" role="button"
            data-ft-info-item-id="${esc(clickId)}"
            data-ft-creature-card-action="open-size-info"
            data-ft-caster-id="${esc(ecId)}"
        >${iconPh(sizeId)}</span></div>`;
    }
    h += '<div class="d-flex align-items-center flex-wrap gap-2 min-w-0" data-ft-creature-classification-tags>';
    for (const layerKey of ['creature_sub_type', 'creature_template'] as const) {
        if (!canControl) continue;
        if (!layerHas(blob, layerKey)) continue;
        const badge = layerKey === 'creature_sub_type' ? 'bg-warning' : 'bg-secondary';
        for (const entry of layerEntriesOf(blob, layerKey)) {
            const uuid = String(layerListEntryUuid(entry) ?? scalarLayerSlot(entry) ?? '');
            if (!uuidOk(uuid)) continue;
            const nested = nestedItemName(entry);
            const label = (typeof nested === 'string' && nested !== '') ? nested : nameOf(uuid);
            if (!label) continue; // no resolvable name → skip the badge rather than print a UUID
            h += `<div class="flex-shrink-0" data-ft-creature-classification-tag><span class="badge ${badge}" role="button" ${ccInfoAttrs(uuid)}>${esc(label)}</span></div>`;
        }
    }
    h += '</div></div>';

    h += '<div class="d-flex align-items-center">';
    if (canControl) {
        const costGp = layerOf(blob, 'cost_gp');
        if (costGp !== null && costGp !== undefined) {
            const n = Number(costGp) || 0;
            h += `<div class="pr-1"><span class="d-inline-block" data-bs-toggle="tooltip" title="Cost GP" data-bs-original-title="Cost GP"><span class="badge bg-warning text-dark">${n === 0 ? 'Priceless' : `${prettyNum(n)} gp`}</span></span></div>`;
        }
        if (layerHas(blob, 'daily_cost_gp')) {
            const n = Number(layerOf(blob, 'daily_cost_gp') ?? 0) || 0;
            h += `<div class="pr-1"><span class="d-inline-block" data-bs-toggle="tooltip" title="Daily Cost GP" data-bs-original-title="Daily Cost GP"><span class="badge bg-warning text-dark">${prettyNum(n)} gp / day</span></span></div>`;
        }
        if (layerHas(blob, 'weight')) {
            const n = Number(layerOf(blob, 'weight') ?? 0) || 0;
            h += `<div class="pr-1"><span class="badge bg-primary">${prettyNum(n)} lb${n > 1 ? 's' : ''}</span></div>`;
        }
        if (layerHas(blob, 'legendary_creature')) {
            h += `<div class="pr-1"><span role="button" ${ccInfoAttrs(INFO_LEGENDARY)}>${iconPh('legendary_creature')}</span></div>`;
        }
        if (layerHas(blob, 'merchant')) {
            h += `<div class="mr-1">${iconPh('merchant')}</div>`;
        }
        if (layerOf(blob, 'trainer')) {
            h += `<div class="mr-1">${iconPh('trainer')}</div>`;
        }
        if (layerHas(blob, 'creature_role')) {
            const roleId = String(layerOf(blob, 'creature_role') ?? '');
            if (roleId) {
                h += `<div class="pr-1"><span role="button" ${ccInfoAttrs(roleId)}>${iconPh(roleId)}</span></div>`;
            }
        }
        const team = bucketScalar(ec, 'team') || String(itemsCol(ec?.team, 'item_id') ?? '');
        if (team) {
            h += `<div class="pr-1" data-ft-creature-header-team><span role="button" ${ccInfoAttrs(String(team))}>${iconPh(team)}</span></div>`;
        }
        const crRaw = layerOf(blob, 'cr');
        if (crRaw !== null && crRaw !== undefined) {
            const cr = Number(crRaw) || 0;
            const crLabel = cr > 0 && cr < 1 ? fracOf(cr) : String(cr);
            h += `<div class="pr-1"><span class="d-inline-block" role="button" data-bs-toggle="tooltip" data-bs-original-title="Challenge Rating" ${ccInfoAttrs(INFO_CR)}><span class="badge bg-danger">${esc(crLabel)}</span></span></div>`;
        }
        // computed.pl is de-scalared to a bare number (2026-06-11); tolerate the legacy {0:n} too.
        const plTotal = powerLevelOf(blob);
        // C2b step 2 (plan §9 amendment 14): an UNPRICED row shows a dash with a pending mark instead of nothing.
        const plComputed = recordOf(blob.computed);
        if (plTotal === undefined || plTotal === null) {
            const pending = !!(plComputed && plComputed.pl_pending);
            h += `<div class="pr-1"><span class="d-inline-block" data-bs-toggle="tooltip" data-bs-original-title="Power Level: ${pending ? 'pending' : 'not priced yet'}"><span class="badge bg-light text-muted ft-pl-pending">—</span></span></div>`;
        }
        if (plTotal !== undefined && plTotal !== null) {
            h += `<div class="pr-1"><span class="d-inline-block" role="button" data-bs-toggle="tooltip" data-bs-original-title="Power Level: ${esc(plExact(plTotal))}"${ccPowerAttrs(ecId)}><span class="badge bg-white text-dark">${esc(plPretty(plTotal))}</span></span></div>`;
        }
    }
    // The object card's 3D marker: thumbnails for everyone, the turntable for a signed-in viewer.
    const meshMarker = renderMeshMarker(blob, names);
    if (meshMarker) h += `<div class="pr-1">${meshMarker}</div>`;
    h += '</div></div></div>';
    return h;
}

// ── DESCRIPTION TAB (port of encounter_creature_description_inc.php) ─────────────────────────────
function buildDescriptionTab(blob: CreatureBlob, ecId: string, canSeeGmFields: boolean): string {
    const ec = recordOf(blob.encounter_creatures) ?? {};
    let d = '';

    const ecFlavor = String(layerOf(blob, 'encounter_creature_flavor_text') ?? '');
    if (ecFlavor !== '') {
        d += `<div id="div_encounter_creature_flavor_text_${esc(ecId)}" class="border border-2 rounded p-3 m-3">${ptMount(ecFlavor)}</div>`;
    }
    const itemFlavor = String(itemsCol(blob, 'flavor_text') ?? '');
    const flavorCookie = cookieVal('cookie_show_flavor_text');
    if (itemFlavor !== '' && ecFlavor === '' && (flavorCookie === null || flavorCookie === '1')) {
        d += `<div id="div_item_flavor_text_${esc(String(itemsCol(blob, 'item_id') ?? ''))}" class="card-text border border-2 rounded p-3 m-3">${ptMount(itemFlavor)}</div>`;
    }

    const gmSections: Array<[string, string, string]> = [
        ['encounter_creature_personality', 'personality', 'Personality'],
        ['encounter_creature_quirks', 'quirks', 'Quirks'],
        ['encounter_creature_motivations', 'motivations', 'Motivations'],
        ['encounter_creature_secrets', 'secrets', 'Secrets'],
        ['encounter_creature_tactics', 'tactics', 'Tactics'],
        ['encounter_creature_gm_notes', 'gm_notes', 'GM Notes'],
    ];
    let anyGm = false;
    if (canSeeGmFields) {
        for (const [field, icon, label] of gmSections) {
            const v = bucketScalar(ec, field);
            if (v === '') continue;
            anyGm = true;
            d += `<hr class="mt-3 mb-3" /><div id="div_${esc(field)}_${esc(ecId)}"><h5 class="card-title">${iconPh(icon)} ${label}</h5>${ptMount(v)}</div>`;
        }
    }

    const itemDescription = String(itemsCol(blob, 'item_description') ?? '');
    if (itemDescription !== '' && canSeeGmFields && !anyGm) {
        d += `<div class="m-1 p-1">${ptMount(itemDescription)}</div>`;
    }

    if (layerHas(blob, 'creature_environment') && canSeeGmFields) {
        const entries = layerEntriesOf(blob, 'creature_environment');
        let envs = '';
        for (const entry of entries) {
            const uuid = String(layerListEntryUuid(entry) ?? scalarLayerSlot(entry) ?? '');
            if (!uuidOk(uuid)) continue;
            envs += `<span class="p-1" role="button" ${ccInfoAttrs(uuid)}>${iconPh(uuid)}</span>`;
        }
        if (envs !== '') {
            d += `<p class="card-text"><span class="fw-bold">Environment${entries.length > 1 ? 's' : ''}:</span>${envs}</p>`;
        }
    }
    if (!canSeeGmFields) return d;
    // Creature-domain layer rows (weapon_essence / mythic_creature / equipment_equipped /
    // do_not_include_automatic_creature_type_monster_bits). The creature console never mounts the full
    // property list, so these layers — which live mostly ON creatures — had no surface at all
    // (weapon_essence on Purple Oni Susei: every offline replay passed, the browser smoke caught it).
    //
    // Built INLINE, deliberately. The first attempt emitted an empty <div data-ft-creature-extra> here
    // and filled it from the render tail via window.renderCreatureExtraLayers — the same pattern the
    // summons mount uses. On staging that div stayed EMPTY (mountExists=true, innerHTML length 0, while
    // calling the renderer by hand in the same page produced 1077 chars), so the one-shot hydration is
    // not reliable for a pane inside the tab strip. Inline HTML is part of the pane itself and cannot be
    // missed or clobbered by a later repaint. Rows follow this tab's own idiom (card-text + iconPh +
    // ccInfoAttrs), matching the Environments row above rather than importing the list-display styling.
    const wEssence = String(layerOf(blob, 'weapon_essence') ?? '');
    if (uuidOk(wEssence)) {
        d += `<p class="card-text"><span class="fw-bold">${iconPh('weapon_essence')} Primary Attacking Essence:</span> `
            + `<span class="p-1" role="button" ${ccInfoAttrs(wEssence)}>${iconPh(wEssence)}</span></p>`;
    }
    const mythicAp = String(layerOf(blob, 'mythic_creature') ?? '');
    if (mythicAp !== '') {
        d += `<p class="card-text"><span class="fw-bold">${iconPh('mythic_creature')} Mythic creature</span> — gains `
            + `<span class="badge bg-dark border border-light">${esc(mythicAp)}</span> ${iconPh('ap')} per turn.</p>`;
    }
    const fixedGear = String(layerOf(blob, 'equipment_equipped') ?? '');
    if (uuidOk(fixedGear)) {
        d += `<p class="card-text"><span class="fw-bold">${iconPh('equipment_equipped')} Permanently Equipped:</span> `
            + `<span class="p-1" role="button" ${ccInfoAttrs(fixedGear)}>${iconPh(fixedGear)}</span></p>`;
    }
    if (layerHas(blob, 'do_not_include_automatic_creature_type_monster_bits')) {
        d += `<p class="card-text text-secondary">${iconPh('monster_bit')} Only this creature’s own monster bits drop — `
            + `the ones normally inherited from its type / sub-type / template are suppressed.</p>`;
    }
    return d;
}

// ── CORE ABILITIES BANNER (port of core_abilities_inc.php, encounter-creature branch) ────────────
function buildCoreAbilitiesBanner(blob: CreatureBlob, ecId: string, sideInfo: boolean): string {
    const ec = recordOf(blob.encounter_creatures) ?? {};
    const teamRaw = ec.team;
    const team = typeof teamRaw === 'object' && teamRaw !== null
        ? String(itemsCol(teamRaw, 'item_id') ?? '')
        : String(teamRaw ?? '');
    const encId = bucketScalar(ec, 'encounter_id');
    const enc = uuidOk(encId) ? window.FateTide?.encounterCache?.get(encId) : undefined;
    const inCombat = enc ? (Number(encounterField(enc, 'in_combat') ?? 0) === 1 ? '1' : '0') : '0';
    const aps = bucketScalar(ec, 'aps') || '0';
    const reaction = bucketScalar(ec, 'reaction') || '0';
    const mana = bucketScalar(ec, 'mana') || '0';

    // use-ability-range buttons share the same data-attr contract as the PHP (the encounter-overlays bundle).
    const rangeBtn = (abilityId: string, ap: string, range: string, dist: string, icon: string, title: string): string => `
        <div class="p-1"><button class="btn btn-info text-dark use-ability-range"
            data-ability_id="${abilityId}" data-item_type="ability" data-ap_required="${ap}"
            data-range="${range}" data-aoe_type="981032B8-4F0B-43C9-8D98-67C693E4DB42"
            data-aoe_distance="${dist}" data-aoe_distance_height="" data-persistent_aoe="0"
            data-aoe_follows_target="0" data-roll_type="AD80D31D-898B-66CE-0894-1335D11B1529"
            data-effect_only_affects_same_team_type="0" data-effect_only_affects_opposing_team_type="0"
            data-aoe_skip_targeting="1" data-caster_team="${esc(team)}" data-caster_in_combat="${inCombat}"
            data-caster_aps="${esc(aps)}" data-caster_reaction="${esc(reaction)}" data-caster_mana="${esc(mana)}"
            title="${esc(title)}">${iconPh(icon.toLowerCase(), 25, 25, title)}</button></div>`;

    const simpleBtn = (abilityId: string, icon: string, btnClass = 'btn btn-info text-dark', extraClass = ''): string => `
        <div class="p-1"><button class="${btnClass}${extraClass}"
            data-ft-creature-card-action="single-ability" data-ft-ability-id="${abilityId}" data-ability_id="${abilityId}"
        >${iconPh(icon)}</button></div>`;

    // render_simple_trigger_ability_button() twin (RESOLVE) — sparkle wrapper + auto-fire toggle badge.
    const resolveActive = triggerIsActive(blob, AB_RESOLVE);
    const resolveBtn = `
        <div class="p-1"><div class="ft-trigger-ability-wrapper${resolveActive ? ' ft-trigger-active' : ''}">
            <button class="btn btn-info text-dark"
                data-ft-creature-card-action="trigger-primary"
                data-ability_id="${AB_RESOLVE}"
                data-ft-trigger-use-snippet="handleAbilityBtnSingleClick('${AB_RESOLVE}');"
                title="Resolve">${iconPh('resolve')}</button>
            <span class="ft-trigger-badge ${resolveActive ? 'ft-trigger-badge-active' : 'ft-trigger-badge-inactive'}"
                title="${resolveActive ? 'Auto-trigger active – click to disable' : 'Auto-trigger inactive – click to enable'}"
                data-ft-creature-card-action="toggle-trigger"
                data-ft-ability-id="${AB_RESOLVE}"
                data-ft-encounter-creature-id="${esc(ecId)}"
                data-ft-encounter-id="${esc(uuidOk(encId) ? encId : '')}"
                data-ft-active="${resolveActive ? '1' : '0'}"></span>
        </div></div>`;

    const bleedGlow = hasStatus(blob, ST_BLEEDING) || hasStatus(blob, ST_MODERATE_BLEEDING) || hasStatus(blob, ST_SEVERE_BLEEDING);

    const w = creatureWindow();
    const userId = window.FateTide?.user?.id ?? w._ft_user_id ?? '';
    const campId = w.ftGetCampaignId?.() || '';
    const clearTriggersBtn = userId ? `
        <div class="p-1"><button type="button" class="btn btn-outline-warning"
            title="Clear trigger overlays for this creature — restores default auto-fire (retaliation, etc.)."
            data-ft-clear-triggers-ec="${esc(ecId)}"
            data-ft-clear-triggers-enc="${esc(uuidOk(encId) ? encId : '')}"
            data-ft-clear-triggers-camp="${esc(uuidOk(campId) ? campId : '')}"
            data-ft-creature-card-action="clear-triggers">${iconPh('clear_triggers')}</button></div>` : '';

    const saTarget = `ft_special_actions_ec_${hexOf(ecId)}`;

    return `
    <div class="ft-core-abilities border rounded px-1${sideInfo ? ' ft-core-abilities--sideinfo-host' : ''}">
        <h5 class="ft-core-abilities-title d-flex align-items-center mt-1 mb-0 py-1 ps-3 fs-6 bg-light text-dark bg-gradient rounded-top">
            <span class="flex-shrink-0 fw-bold">Core Abilities</span>
            <span class="ft-ability-view-toolbar-host ft-core-abilities-toolbar ms-2 d-inline-flex align-items-center" style="min-width:0;"></span>
        </h5>
        <div class="row m-0">
            <div class="d-flex align-items-center justify-content-start col-md-12 mt-2">
                <div class="ft-core-ability-group d-flex flex-wrap align-items-center gap-1 mb-2">
                    ${rangeBtn(AB_SPOT_HIDDEN, '2', 'F6DAE84B-CBB3-5C0F-89EF-370DE841DFDA', '20', 'spot_hidden_creatures', 'Spot Hidden Creatures')}
                    ${rangeBtn(AB_SEARCH_HIDDEN_INV, '3', '36D59FF4-6778-96ED-8664-F5D63638C77B', '10', 'search_for_hidden_inventory', 'Search for Hidden Inventory')}
                    ${resolveBtn}
                    ${simpleBtn(AB_SNAP_OUTTA_IT, 'snap_outta_it')}
                    ${simpleBtn(AB_STOP_BLEEDING, 'stop_the_bleeding', 'btn btn-info text-dark', bleedGlow ? ' ft-stop-bleeding-active' : '')}
                </div>
            </div>
            <div class="col-md-12 mt-2">
                <div class="ft-core-ability-group d-flex flex-wrap align-items-center gap-1 mb-2">
                    ${simpleBtn(AB_CUSTOM_FULL, 'custom_full_ability', 'btn btn-muted')}
                    ${simpleBtn(AB_CUSTOM, 'custom_ability', 'btn btn-muted')}
                    ${simpleBtn(AB_CUSTOM_QUICK, 'custom_quick_ability', 'btn btn-muted')}
                    ${simpleBtn(AB_CUSTOM_FREE, 'custom_free_ability', 'btn btn-muted')}
                    ${simpleBtn(AB_CUSTOM_REACTION, 'custom_reaction_ability', 'btn btn-muted')}
                    <div class="p-1"><button class="btn btn-danger-soft" data-ft-creature-card-action="add-ap">${iconPh('add_ap')}</button></div>
                    <div class="p-1"><button class="btn btn-danger-soft" data-ft-creature-card-action="add-reaction">${iconPh('add_reaction')}</button></div>
                    ${clearTriggersBtn}
                </div>
            </div>
            <div id="${saTarget}" class="col-12 mt-2"></div>
        </div>
    </div>`;
}

// ── ABILITIES TAB WRAPPER (port of _abilities_inc.php) ───────────────────────────────────────────
function buildAbilitiesTab(blob: CreatureBlob, ecId: string, sideInfo: boolean, canEdit: boolean): string {
    const rootId = `ft_sheet_abilities_${ecId.replace(/[^A-Za-z0-9_-]/g, '_')}`;
    const searchId = `${rootId}_search`;
    // The Core Abilities banner bakes LIVE combat state into its buttons (AP/mana/reaction/in_combat
    // gating attrs on the use-ability-range buttons, the stop-bleeding glow, the resolve auto-trigger
    // state) plus it hosts the special-actions strip. Mount it as a .ft-pc-sheet-pane so
    // ftRerenderPcSheet repaints it on caster_update — without this the gating attrs went stale after
    // an AP/mana spend (the card skips the legacy refreshCasterSheet). The non-edit toolbar bar has no
    // live state, so it stays static.
    const banner = canEdit
        ? paneMount('creature_core_abilities', ecId, ` data-ft-pc-side-info="${sideInfo ? '1' : '0'}"`)
        : '<div class="ft-ability-view-toolbar-bar d-flex align-items-center mb-2"><span class="ft-ability-view-toolbar-host"></span></div>';

    let summonsMount = '';
    if (
        layerHas(blob, 'shapeshift') || layerHas(blob, 'summon') || layerHas(blob, 'summon_type')
        || layerHas(blob, 'cast_spell_through_summon_range') || layerHas(blob, 'at_will_spell')
        || layerHas(blob, 'innate_spell_per_long_rest')
        || layerHas(blob, 'lair') || layerHas(blob, 'regional_effect')
    ) {
        // Hydrated post-injection via window.renderCreatureSummonsLists(blob, names, el) — same
        // renderer the PHP ft_ld_ts_creature_summons_mount island feeds, minus the inline JSON copy.
        summonsMount = `<div id="ft_creature_summons_${hexOf(ecId)}" data-ft-creature-summons="1"></div>`;
    }

    // Keyed host so ftRerenderClassMechanicBars repaints the (non-pane) charge bars on caster_update —
    // same reason as the PC card's abilities tab (see buildPcAbilitiesTab). [[live charge sync]]
    return `<div data-ft-class-mechanic-host="${esc(ecId)}" data-ft-sideinfo="${sideInfo ? '1' : '0'}" data-ft-canedit="${canEdit ? '1' : '0'}">${buildClassMechanicBars(blob, sideInfo, canEdit)}</div>${ABILITIES_TAB_STYLE}
    <div class="fate-tide-sheet-abilities-search-root${sideInfo ? ' ft-abilities--sideinfo-host' : ''}" id="${rootId}">
        <div class="ft-abilities-header-wrap">
            ${banner}
            <div class="row g-2 align-items-center my-3 w-100 mx-0 ft-abilities-search-row">
                <div class="col-12 col-md-auto ms-md-auto ft-abilities-search-anchor">
                    <label class="visually-hidden" for="${searchId}">Search abilities</label>
                    <div class="position-relative">
                        <input type="search" id="${searchId}" class="form-control form-control-sm fate-tide-sheet-ability-search pe-4"
                            data-ft-sheet-abilities-root="${rootId}" placeholder="Search abilities…" autocomplete="off">
                        <svg class="svg-icon position-absolute top-50 end-0 translate-middle-y me-2 ft-sheet-search-icon" aria-hidden="true"><use href="#search"></use></svg>
                    </div>
                </div>
            </div>
        </div>
        ${paneMount('ability_sections', ecId, ' data-ft-static-creature="0"')}
        ${summonsMount}
    </div>`;
}

// ── PASSIVE ABILITIES TAB WRAPPER ────────────────────────────────────────────────────────────────
// The passive tree (passive-abilities.ts) is built to be searched: every row carries
// .fate-tide-sheet-ability-row + data-ft-ability-name, and its search↔collapse truce keys off the
// enclosing .fate-tide-sheet-abilities-search-root. Wrap the pane in that root + a search input so
// filter-search-init.js (fateTideApplySheetAbilitySearchInWrap) filters it — exactly like the
// abilities/weapons tabs. The input lives in THIS stable wrapper (NOT the WS-repainted pane) so a
// query survives caster_update repaints. Desktop CSS (95-pc-sheet.css, .ft-passive-abilities-search-
// root) overlays it onto the blue "Passive Abilities" band, right-aligned. Reused by the PC card.
export function buildPassiveAbilitiesSearchWrap(scopeId: string, paneHtml: string): string {
    const rootId = `ft_sheet_passive_abilities_${scopeId.replace(/[^A-Za-z0-9_-]/g, '_')}`;
    const searchId = `${rootId}_search`;
    return `<div class="fate-tide-sheet-abilities-search-root ft-passive-abilities-search-root" id="${rootId}">
        <div class="row g-2 align-items-center my-3 w-100 mx-0 ft-passive-search-row">
            <div class="col-12 col-md-auto ms-md-auto ft-abilities-search-anchor">
                <label class="visually-hidden" for="${searchId}">Search passive abilities</label>
                <div class="position-relative">
                    <input type="search" id="${searchId}" class="form-control form-control-sm fate-tide-sheet-ability-search pe-4"
                        data-ft-sheet-abilities-root="${rootId}" placeholder="Search passive abilities…" autocomplete="off">
                    <svg class="svg-icon position-absolute top-50 end-0 translate-middle-y me-2 ft-sheet-search-icon" aria-hidden="true"><use href="#search"></use></svg>
                </div>
            </div>
        </div>
        ${paneHtml}
    </div>`;
}

// ── WEAPONS TAB WRAPPER (port of _weapons_equipped_inc.php live structure) ───────────────────────
// Exported: the PC card (render-player-card.ts) reuses it unchanged — the PHP include is shared
// between the two sheet paths too. (PC parity note: the PHP PC path skips the natural_weapons
// pane when a RAW natural_weapon layer exists; the PC card gates that case to the server.)
export function buildWeaponsTab(blob: CreatureBlob, ecId: string, sideInfo: boolean): string {
    const rootId = `ft_sheet_core_attacks_${ecId.replace(/[^A-Za-z0-9_-]/g, '_')}`;
    const searchId = `${rootId}_search`;

    let attacksBlock = '';
    if (ftLayerEntries(blob, 'attack_ability').length > 0 || ftLayerEntries(blob, 'opportunity_attack_ability').length > 0) {
        attacksBlock = `
        <div class="ft-attacks-banner-wrap">
            <div class="row g-2 align-items-center my-3 w-100 mx-0 ft-attacks-search-row">
                <div class="col-12 col-md-auto ms-md-auto ft-attacks-search-anchor">
                    <label class="visually-hidden" for="${searchId}">Search attack abilities</label>
                    <div class="position-relative">
                        <input type="search" id="${searchId}" class="form-control form-control-sm fate-tide-sheet-ability-search pe-4"
                            data-ft-sheet-abilities-root="${rootId}" placeholder="Search attacks…" autocomplete="off">
                        <svg class="svg-icon position-absolute top-50 end-0 translate-middle-y me-2 ft-sheet-search-icon" aria-hidden="true"><use href="#search"></use></svg>
                    </div>
                </div>
            </div>
            ${paneMount('weapon_abilities', ecId, ' data-ft-static-creature="0"')}
        </div>`;
    }

    return `${WEAPONS_TAB_STYLE}
    <div class="fate-tide-sheet-abilities-search-root${sideInfo ? ' ft-weapons-equipped--sideinfo-host' : ''}" id="${rootId}">
        ${paneMount('core_attacks', ecId)}
        ${paneMount('natural_weapons', ecId)}
        ${paneMount('equipped_weapons', ecId, ' data-ft-static-creature="0"')}
        ${attacksBlock}
    </div>`;
}

// (ABILITIES_TAB_STYLE / WEAPONS_TAB_STYLE moved to card-shell.ts — shared with the PC card.)

// ── SKINNING TAB (port of the console's dead branch + skinning.php — VIEW only) ──────────────────
// The integrated monster-bit tiles render from the blob's own inventory bucket (skinning.php reads
// nothing else); the skinning ROLL stays server-side — dragging a tile out of the dropzone fires
// the existing RNG endpoints (refreshDragAndDrop wires it in hydration). Known v1 parity gap: the
// GM harvest-yield reference blocks (monster_bit / attached_loot_table list-display cards) — that
// renderer lives in the list-display bundle, outside this bundle's import boundary.
function buildSkinningTab(blob: CreatureBlob, ecId: string, sideInfo: boolean): string {
    const iconSize = Number(cookieVal('inventory_icon_size') || 70) || 70;
    let tiles = '';
    for (const entry of layerEntriesOf(blob, 'inventory')) {
        if (!entry || typeof entry !== 'object') continue;
        if (layerHas(entry, 'equipped')) continue; // On Hand only
        if (String(inventoryField(entry, 'possess_id') ?? '').toUpperCase() !== ecId.toUpperCase()) continue;
        if (String(itemsCol(entry, 'item_type') ?? '') !== 'monster_bit') continue;
        if (!nodeHasStatusItemId(entry, 'target', INTEGRATED_OBJECT_ITEM_ID)) continue; // still integrated
        tiles += inventoryTileMount(entry, iconSize);
    }
    const inner = tiles !== ''
        ? tiles
        : '<div class="mt-3" style="min-height: 50px;"><h5>No Skinnable Monster Bits</h5></div>';
    return `<div class="row px-3">
        <div id="div_skinning_inventory_encounter_creature" class="${sideInfo ? 'col-12' : 'col-lg-6 col-md-12'}">
            <h5 class="mb-0 pt-2 pb-2 bg-secondary text-dark bg-gradient rounded-top text-center">Skinning</h5>
            <div id="skinning_inventory_${esc(ecId)}" class="d-flex justify-content-center flex-wrap border rounded-bottom dropzone">${inner}</div>
        </div>
    </div>`;
}

// True when the caster carries any menta. Reads the curated computed.creature.abilities.menta bucket
// DIRECTLY — the module layer readers (array-types) have no abilities bridge and mentas are not exposed
// via a default-provider carrier, so layerHas/layerOf miss them. layerHas kept as a legacy-shape fallback.
export function hasMentaBucket(blob: CreatureBlob): boolean {
    const m = recordOf(computedCreature(blob)?.abilities)?.menta;
    if (Array.isArray(m) && m.length > 0) return true;
    return layerHas(blob, 'menta');
}

// ── CONSOLE TABS (port of _creature_console_tabs_inc.php, live-EC branch) ────────────────────────
const PC_TAB_SUFFIX_TO_CREATURE: Record<string, string> = {
    skills: 'creature_skills',
    abilities: 'creature_abilities',
    weapons_equipped: 'creature_weapons_equipped',
    movement: 'creature_movement',
    passive_abilities: 'creature_passive_abilities',
    castable_spells: 'creature_castable_spells',
    prepare_spells: 'creature_castable_spells',
    mentas: 'creature_mentas',
    inventory: 'creature_inventory',
    character_description: 'creature_description',
    defenses: 'creature_defenses',
};

const socialViewerRefusals = new Set<string>();
export function buildCreatureSocialActions(blob: CreatureBlob, ecId: string, viewerId: string): string {
    // The GM viewer acts as the inspected target, even while a home PC remains set.
    const actingId = String(creatureWindow().ftGetActiveCasterId?.() ?? '');
    if (!uuidOk(viewerId) || viewerId.toUpperCase() === ecId.toUpperCase()
        || actingId.toUpperCase() === ecId.toUpperCase() || creatureDisplayAccess(blob).loot) return '';
    if (!window.FateTide?.casterCache?.has(viewerId)) creatureWindow().ftSeedCasterFromEncounterCache?.(viewerId);
    const viewer = recordOf(window.FateTide?.casterCache?.get(viewerId) ?? window.FateTide?.objectCache?.get(viewerId));
    if (!viewer) {
        const reason = `unported:creature-display:social-actions:viewer-not-held:${viewerId}`;
        if (!socialViewerRefusals.has(viewerId)) { socialViewerRefusals.add(viewerId); refuseUnported(reason); }
        return `<span class="text-danger" data-ft-display-refusal>${esc(reason)}</span>`;
    }
    if (!hasPlayerPermission(viewer) || creatureDisplayAccess(viewer).loot
        || encounterCreatureField(viewer.encounter_creatures, 'encounter_id') !== encounterCreatureField(blob.encounter_creatures, 'encounter_id')) return '';
    const trade = layerHas(blob, 'merchant'), train = layerHas(blob, 'trainer');
    const button = (action: string, icon: string, otherMode = false): string =>
        `<button type="button" class="btn btn-sm btn-info" aria-label="${esc(action)}" ${ccActionAttrs(action,
            { targetId: ecId, viewerId, otherMode: otherMode ? '1' : '0' })}>${iconPh(icon, 25, 25, action)}</button>`;
    return `<div class="mb-2 d-flex flex-wrap align-items-center gap-1">${button('pickpocket', 'pickpocket')}${trade ? button('trade', 'merchant', train) : ''}${train ? button('train', 'trainer', trade) : ''}</div>`;
}

function buildConsoleTabs(blob: CreatureBlob, ecId: string, suffix: string, sideInfo: boolean, viewerMainId: string, controlVerdict?: boolean): string {
    // The card passes its target-row verdict; ordinary cache callers use stamped permissions.
    const access = creatureDisplayAccess(blob, viewerMainId, controlVerdict);
    const canEdit = access.control;
    const isDead = access.dead;
    const spellsPrepared = recordOf(computedCreature(blob)?.spells_prepared);
    const hasSpellsPrepared = !!spellsPrepared && Object.keys(spellsPrepared).length !== 0;
    // A lair is an item-like combat actor: it reuses the console SHELL but only the tabs that apply —
    // Abilities, Castable Spells, Description, GM. Skills / weapons / movement / mentas / inventory /
    // defenses / skinning / status-effects are creature-only and are hidden (and their panes skipped).
    const isLair = String(itemsCol(blob, 'item_type') ?? '').toLowerCase() === 'lair';

    const tabs: Array<{ key: string; show: boolean }> = [
        // Status-effect panel — encounter creatures, ALL viewers (private statuses are filtered for
        // unpermitted viewers inside the info-panel island). First pill so it reads "near the top",
        // restoring the status section the unified-renderer transition dropped from the EC card.
        { key: 'creature_status_effects', show: !isLair },
        { key: 'creature_description', show: true },
        { key: 'creature_skills', show: canEdit && !isLair },
        { key: 'creature_abilities', show: canEdit },
        { key: 'creature_weapons_equipped', show: canEdit && !isLair },
        { key: 'creature_movement', show: canEdit && !isLair },
        { key: 'creature_passive_abilities', show: canEdit && !isLair && layerHas(blob, 'passive_ability') },
        {
            // Castable Spells reads computed.creature.castable (baked at finalize — creature-only today).
            // For a lair, show it ONLY once that payload exists so we never render an empty tab; the
            // targeted lair castable-bake is the follow-up that populates it (its at-will spells then appear).
            key: 'creature_castable_spells',
            show: canEdit && (isLair
                ? castableHasEntries(blob)
                : (layerHas(blob, 'spell_prepared_1') || layerHas(blob, 'spell_prepared_2')
                    || layerHas(blob, 'spell_prepared_3') || layerHas(blob, 'at_will_spell')
                    || layerHas(blob, 'innate_spell_per_long_rest') || hasSpellsPrepared)),
        },
        // Mentas relocated to computed.creature.abilities.menta as curated {source,item_id} refs.
        // Read that bucket DIRECTLY: the module layer readers have no abilities bridge, and mentas are
        // not exposed through a default-provider carrier (unlike class/ancestry-carried passive_ability),
        // so layerHas/layerOf both miss them. layerHas kept as a fallback for any legacy top-level shape.
        // [[project_caster_arr_source_table_rebuild]]
        { key: 'creature_mentas', show: canEdit && !isLair && hasMentaBucket(blob) },
        { key: 'creature_inventory', show: access.inventory && !isLair },
        // creature_skinning: unlocks on death (live-EC branch; the static-item-creature case is
        // the PHP statblock path, never this card).
        { key: 'creature_skinning', show: !isLair && isDead },
        { key: 'creature_defenses', show: canEdit && !isLair },
        // FAMILY — the placement's genealogy. A lair is an environmental actor, not a creature with
        // parents, so it never gets the tab. Everything else defers to the server's stamp.
        { key: 'creature_family', show: !isLair && kinTabVisible(blob) },
        // GM action strip (edit/hide/private/kill/destroy/delete) — encounter creatures, GM only.
        // Placed LAST in GM view so the GM controls sit at the end of the tab strip, not up front.
        { key: 'creature_gm', show: canEdit },
    ];
    const visible = tabs.filter((t) => t.show).map((t) => t.key);

    // Active tab from the shared sheet cookie (same mapping as the PHP).
    let active = 'creature_abilities';
    const rawCookie = cookieVal('cookie_player_character_sheet_menu_tab');
    if (rawCookie) {
        const sfx = rawCookie.replace(/^tab_/, '');
        if (sfx.startsWith('creature_')) active = sfx;
        else if (PC_TAB_SUFFIX_TO_CREATURE[sfx]) active = PC_TAB_SUFFIX_TO_CREATURE[sfx]!;
    }
    if (!visible.includes(active)) {
        active = visible.includes('creature_abilities') ? 'creature_abilities' : 'creature_description';
    }

    // A restricted player commonly sees only Status + Description. Keep that small information
    // pair on one row; larger consoles retain the compact two-row layout.
    const colsPerRow = visible.length <= 2
        ? Math.max(1, visible.length)
        : Math.max(1, Math.ceil(visible.length / 2));
    let pills = '';
    for (const key of visible) {
        // 'creature_status_effects' → 'status_effects' (plural, unregistered); use the panel's own icon.
        // 'creature_family' likewise uses its own `family_tree` glyph, not 'ancestry'.
        const icon = key === 'creature_status_effects'
            ? 'show_status_effects'
            : key === 'creature_family' ? 'family_tree' : key.replace(/^creature_/, '');
        const isActive = key === active;
        pills += `<li class="nav-item p-1" role="presentation">
            <button id="tab_${key}${suffix}" class="nav-link border ${isActive ? 'active' : ''}"
                data-bs-target="#div_${key}${suffix}" aria-controls="div_${key}${suffix}"
                data-bs-toggle="pill" type="button" role="tab" ${isActive ? "aria-selected='true'" : ''}
                data-ft-creature-card-action="set-tab-cookie"
                data-ft-tab="${key}"
                data-ft-assert-caster-id="${esc(ecId)}"
            >${iconPh(icon)}</button>
        </li>`;
    }

    const pane = (key: string, inner: string, extraClass = ''): string => `
        <div id="div_${key}${suffix}" class="tab-pane fade ${key === active ? 'show active' : ''}${extraClass}"
            aria-labelledby="tab_${key}${suffix}" role="tabpanel">${inner}</div>`;

    let panes = '';
    // Status-effect panel — a PC-sheet pane (data-ft-pc-pane="creature_status_effects") so ftMountPcSheet
    // paints it AND ftRerenderPcSheet repaints it on caster_update (add / delete a status → live update),
    // same as the creature_summary / creature_header panes. Renderer bridges to the info-panel island.
    if (!isLair) panes += pane('creature_status_effects', paneMount('creature_status_effects', ecId));
    panes += pane('creature_description', buildDescriptionTab(blob, ecId, canEdit));
    if (visible.includes('creature_skills')) panes += pane('creature_skills', paneMount('skills', ecId));
    // The shared ability pane resolves creature and lair definitions from held catalogs.
    if (visible.includes('creature_abilities')) panes += pane('creature_abilities', buildAbilitiesTab(blob, ecId, sideInfo, canEdit));
    if (visible.includes('creature_weapons_equipped')) panes += pane('creature_weapons_equipped', buildWeaponsTab(blob, ecId, sideInfo));
    if (visible.includes('creature_movement')) panes += pane('creature_movement', paneMount('movement', ecId));
    if (visible.includes('creature_passive_abilities')) {
        panes += pane('creature_passive_abilities', buildPassiveAbilitiesSearchWrap(ecId, paneMount('passive_abilities', ecId, ' data-ft-static-creature="0"')));
    }
    if (visible.includes('creature_castable_spells')) {
        panes += pane('creature_castable_spells', paneMount('castable_spells', ecId));
    }
    if (visible.includes('creature_mentas')) {
        panes += pane('creature_mentas', paneMount('mentas', ecId));
    }
    if (visible.includes('creature_inventory')) panes += pane('creature_inventory', paneMount('inventory', ecId, ` data-ft-viewer-caster-id="${esc(viewerMainId)}"`));
    if (visible.includes('creature_skinning')) {
        panes += pane('creature_skinning', buildSkinningTab(blob, ecId, sideInfo));
    }
    if (visible.includes('creature_defenses')) panes += pane('creature_defenses', paneMount('defenses', ecId), ' text-left');
    if (visible.includes('creature_family')) {
        panes += pane('creature_family', buildKinFamilyPane(blob, ecId), ' text-left');
    }
    if (visible.includes('creature_gm')) {
        // GM action strip — mount as a PC-sheet pane so the SAME ftMountPcSheet scanner that paints every
        // other tab hydrates it (and ftRerenderPcSheet repaints it on caster_update → kill↔resurrect flips
        // live on death). Replaces the old plain-div + explicit-call mount; the PHP card uses the identical
        // data-ft-pc-pane="creature_gm" mount, so the server/AJAX path (a summon falling back to PHP) now
        // fills the GM tab too instead of leaving it blank.
        panes += pane('creature_gm', `<div class="ft-pc-sheet-pane pt-2" data-ft-pc-pane="creature_gm" data-ft-caster-id="${esc(ecId)}"></div>`);
    }

    return `
    <div${sideInfo ? ' class="mt-2"' : ''}>
        <ul class="nav nav-pills pt-1 pb-1 border border-bottom-0 rounded-top character-console-nav${sideInfo ? ' character-console-nav--sideinfo' : ''}"
            id="nav_tabs_character_console${suffix}" role="tablist"
            ${sideInfo ? `style="--ft-console-cols: ${colsPerRow};"` : ''}>${pills}</ul>
        <div class="tab-content border overflow-auto pl-1 pr-1" style="height: 595px; scrollbar-width: thin;">${panes}</div>
    </div>`;
}

// Lairs: the compile leans their ability defs to unreadable {source,item_id} shells AND the encounter
// assembly strips computed.creature.abilities off the realiased member blob — so the console ability
// pane resolves nothing reliably (nameless / dropped rows). Rebuild computed.creature.abilities
// CLIENT-SIDE from the reliable global finder (window.ftFindFullNestedItem walks casterCache /
// objectCache / encounterCache incl. the encounter root .catalog — the SAME source the list-display
// resolved names/runes from), embedding each ability's FULL def as item_arr so the pane reads
// name/rune/cast DIRECTLY with no further resolution. Mutates the cache blob in place BEFORE
// ftMountPcSheet paints the ability_sections pane. at_will_spell excluded (Castable Spells tab).
// True only when the baked castable payload carries at least one non-empty spell list. The bake OMITS
// empty lists (castable-spells.ts SpellsPayload), so a lair with only lair_ability (no spells) still
// gets a truthy `castable` object (can_edit/max_spell_level only) — `!!castable` would wrongly show an
// empty Castable Spells tab. Gate lairs on real entries so the tab is hidden when they have no spells.
export function castableHasEntries(blob: CreatureBlob): boolean {
    const castable = recordOf(computedCreature(blob)?.castable);
    if (!castable) return false;
    for (const k of ['spells_known', 'curses_known', 'at_will_spell', 'innate_spell_per_long_rest', 'charged_spell']) {
        const entries = castable[k];
        if (Array.isArray(entries) && entries.length > 0) return true;
    }
    return false;
}

export function lairPopulateAbilitiesFromCatalog(blob: CreatureBlob): void {
    // Use the DIRECT import findFullNestedItem (find-cached-item.ts), NOT window.ftFindFullNestedItem —
    // that window attach belongs to another bundle and may not exist at sheet-render time (ability-row.ts
    // hit the same trap). This walks casterCache/objectCache/encounterCache incl. the encounter root catalog.
    const computed = ensureRecord(blob, 'computed');
    const creature = ensureRecord(computed, 'creature');
    const abilities = ensureRecord(creature, 'abilities');
    const TYPES = ['lair_ability', 'quick_ability', 'attack_ability', 'free_ability', 'reaction_ability', 'full_ability', 'ability', 'opportunity_attack_ability'];
    for (const t of TYPES) {
        const entries = layerEntriesOf(blob, t);
        if (!entries || entries.length === 0) continue;
        const out: CreatureBlob[] = [];
        const seen: Record<string, boolean> = {};
        for (const e of entries) {
            const entry = recordOf(e);
            const id = (typeof e === 'string')
                ? e
                : String(entry?.item_id ?? entry?.ability_id ?? itemsCol(entry, 'item_id') ?? '');
            if (!id) continue;
            const key = id.toLowerCase();
            if (seen[key]) continue;
            seen[key] = true;
            let full: CreatureBlob | null = null;
            try { full = findFullNestedItem(id); } catch (err) { full = null; }
            if (!full || !recordOf(full.item_layer_types)) continue; // need the real def
            const rebuilt: CreatureBlob = {
                ability_id: id,
                item_name: nestedItemName(full),
                source: id,
                item_arr: full,
            };
            // Keep the server-stamped live charge state ({current,max} from the charges DB row —
            // lair refactor Section 2). Without it the ability-row falls back to the declared
            // ability_charges layer and always renders the max, hiding spent charges.
            const charges = recordOf(entry?.charges);
            if (charges) rebuilt.charges = charges;
            out.push(rebuilt);
        }
        if (out.length) abilities[t] = out;
    }
}

// ── NAMES MAP (header chips) ───────────────────────────────────────────────────────────────────
// Blob's own catalog, gap-filled from the encounter ROOT catalog: realiased member rows carry no
// catalog of their own, so their classification refs (e.g. monster_template-added creature_templates)
// resolve their display name only against the root. first-writer-wins preserves the member's own
// fuller harvest. Shared by the main render and the creature_header pane re-render.
function buildCardNamesMap(blob: CreatureBlob): ReferencedNames {
    const names = buildNamesMapFromBlob(blob);
    const encIdForNames = bucketScalar(recordOf(blob.encounter_creatures) ?? {}, 'encounter_id');
    const encForNames = uuidOk(encIdForNames)
        ? recordOf(window.FateTide?.encounterCache?.get(encIdForNames))
        : null;
    const catalog = recordOf(encForNames?.catalog);
    if (catalog) {
        const rootNames = buildNamesMapFromBlob({ catalog });
        for (const k of Object.keys(rootNames)) {
            const rootName = rootNames[k];
            if (names[k] === undefined && rootName) names[k] = rootName;
        }
    }
    return names;
}

// ── LIVE-STATE PANE RENDERERS ────────────────────────────────────────────────────────────────────
// The header (x/z coords, power level) and the Core Abilities banner (AP/mana/reaction/in_combat
// gating attrs, bleed glow, resolve auto-trigger state, + the special-actions strip it hosts) bake
// mutable combat state into the card. They're mounted as .ft-pc-sheet-panes (paneMount) so
// ftRerenderPcSheet repaints them on caster_update / caster_update_delta — the card sets
// data-ft-pc-owns-sheet="1" and skips the legacy refreshCasterSheet, so without this they go stale.
// The PC-sheet pane router dispatches to these via window (same seam as renderCreatureSummaryInline).
function resolveTargetBlob(casterId: string, container: HTMLElement | string): { target: HTMLElement; blob: CreatureBlob; ecId: string } | null {
    const target = typeof container === 'string' ? document.getElementById(container) : container;
    const blob = recordOf(window.FateTide?.casterCache?.get(casterId));
    if (!target || !blob) return null;
    const ecId = String(encounterCreatureField(blob.encounter_creatures, 'encounter_creature_id') ?? '');
    if (!uuidOk(ecId)) return null;
    return { target, blob, ecId };
}

function ftRenderCreatureHeaderFromCache(casterId: string, container: HTMLElement | string): boolean {
    const target = typeof container === 'string' ? document.getElementById(container) : container;
    const blob = recordOf(window.FateTide?.casterCache?.get(casterId) ?? window.FateTide?.objectCache?.get(casterId));
    if (!target || !blob) return false;
    const names = buildCardNamesMap(blob);
    const ecId = String(encounterCreatureField(blob.encounter_creatures, 'encounter_creature_id') ?? '');
    const verdict = target.closest<HTMLElement>('[data-ft-viewer-can-control]')?.dataset.ftViewerCanControl;
    // An unchanged header keeps its nodes and the icons already mounted in them.
    if (paintMarkup(target, uuidOk(ecId) ? buildHeader(blob, ecId, names, verdict === undefined ? undefined : verdict === '1') : buildStaticHeader(blob, casterId, names))) window.ftMountIcons?.(target);
    return true;
}

function ftRenderCreatureCoreAbilitiesFromCache(casterId: string, container: HTMLElement | string, sideInfo = false): boolean {
    const r = resolveTargetBlob(casterId, container);
    if (!r) return false;
    const w = creatureWindow();
    const painted = paintMarkup(r.target, buildCoreAbilitiesBanner(r.blob, r.ecId, !!sideInfo));
    // Re-hydrate the islands the banner hosts (mirror of the main-render hydration pass): the
    // special-actions strip, the ability-view toolbar, and the icon / rune-icon placeholders.
    // The strip reads its own state, so it is asked even when the banner holds (it keeps itself when unchanged).
    try {
        const sa = r.target.querySelector(`#ft_special_actions_ec_${hexOf(r.ecId)}`) as HTMLElement | null;
        if (sa) w.renderCasterSpecialActionsInline?.(r.ecId, sa.id, true);
    } catch (e) { /* non-fatal */ }
    if (!painted) return true;
    try { w.ftMountAbilityViewToolbars?.(); } catch (e) { /* non-fatal */ }
    try { w.ftMountIcons?.(r.target); } catch (e) { /* non-fatal */ }
    try { w.ftMountRuneIcons?.(r.target); } catch (e) { /* non-fatal */ }
    return true;
}

// ── MAIN ENTRY ───────────────────────────────────────────────────────────────────────────────────
export interface CreatureCardOpts extends EntityCardPaintHooks {
    viewerCanControl?: boolean;
    viewerCasterId?: string;
    readOnlyInspect?: boolean;
    sideInfo?: boolean;
    cardColClasses?: string;
    /** Unique full-sheet window suffix. Empty preserves the canonical controlled-actor DOM ids. */
    domSuffix?: string;
    // Explicit inspect target (caster-persistence fix 2026-06-22): the GM viewer renders a creature
    // the user is merely INSPECTING, which is no longer the same as #caster_id. When this matches the
    // card's creature, the self-view gate below renders from cache instead of falling back to the PHP
    // shell — view no longer rides on the (now-decoupled) active caster.
    viewTarget?: string;
}

const mountedCreatureCards = new WeakMap<HTMLElement, { opts: CreatureCardOpts; access?: string }>();
function accessKey(blob: CreatureBlob, viewer: string, verdict?: boolean): string {
    const access = creatureDisplayAccess(blob, viewer, verdict);
    return `${access.control}:${access.inventory}:${access.dead}`;
}

/** Compilation and access changes can replace the shell, not just its panes. */
function ftRefreshCreatureCardAccess(casterId: string): void {
    const blob = recordOf(window.FateTide?.casterCache?.get(casterId));
    if (!blob) return;
    document.querySelectorAll<HTMLElement>('[data-ft-creature-card-refused]').forEach(target => {
        if (target.dataset.ftCreatureCardRefused !== casterId || typeof blob._entity_refusal === 'string') return;
        const state = mountedCreatureCards.get(target);
        if (state) ftRenderCreatureCardFromCache(casterId, target, state.opts);
    });
    document.querySelectorAll<HTMLElement>('[data-ft-creature-card-cache="1"]').forEach(card => {
        if (card.dataset.ftCasterId !== casterId || !card.parentElement) return;
        const target = card.parentElement;
        const state = mountedCreatureCards.get(target);
        if (!state) return;
        const verdict = state.opts.viewerCanControl ?? (ftIsModifyEncounterToolsPage() ? true : undefined);
        const key = accessKey(blob, String(state.opts.viewerCasterId ?? creatureWindow().ftGetMasterCasterId?.() ?? ''), verdict);
        if (key !== state.access) ftRenderCreatureCardFromCache(casterId, target, state.opts);
    });
}

function ftRenderCreatureCardFromCache(
    casterId: string,
    container: HTMLElement | string,
    opts: CreatureCardOpts = {},
): boolean {
    try {
        const target = typeof container === 'string' ? document.getElementById(container) : container;
        if (!casterId || !target) return false;

        // Required hydrators — without the pane mounter the card would be an empty shell.
        const w = creatureWindow();
        if (typeof w.ftMountPcSheet !== 'function') return false;

        let blob = recordOf(window.FateTide?.casterCache?.get(casterId));
        if (!blob && typeof w.ftSeedCasterFromEncounterCache === 'function') {
            w.ftSeedCasterFromEncounterCache(casterId);
            blob = recordOf(window.FateTide?.casterCache?.get(casterId));
        }
        if (!blob) return false;
        if (holdPendingEntityCard(blob, target, compiled => {
            if (window.FateTide?.casterCache?.get(casterId) === blob) window.FateTide.casterCache.set(casterId, compiled);
            return ftRenderCasterCardFromCache(casterId, target, opts);
        }, { onPaintUnavailable: opts.onPaintUnavailable, onFailure: () => {
            target.dataset.ftCreatureCardRefused = casterId;
            mountedCreatureCards.set(target, { opts });
            opts.afterPaint?.();
        } })) return true;
        if (typeof blob._entity_refusal === 'string') {
            const id = String(encounterCreatureField(blob.encounter_creatures, 'encounter_creature_id') ?? '');
            const repair = creatureDisplayAccess(blob).control && uuidOk(id)
                ? `<div class="ft-pc-sheet-pane pt-2" data-ft-pc-pane="creature_gm" data-ft-caster-id="${esc(id)}"></div>` : '';
            replaceHydratedHtml(target, `<div class="alert alert-warning" role="alert">${esc(blob._entity_refusal)}</div>${repair}`);
            // A page can mount this saved-row shell before its browser compile settles.
            // Keep its context so the normal compiler repaint can install the complete card.
            target.dataset.ftCreatureCardRefused = casterId;
            mountedCreatureCards.set(target, { opts });
            if (repair) w.ftMountPcSheet(target);
            opts.afterPaint?.();
            return true;
        }

        const _blobType = String(encounterCreatureField(blob.encounter_creatures, 'item_type') ?? itemsCol(blob, 'item_type') ?? '').toLowerCase();
        // Lairs reuse the creature console (a lair-scoped tab subset — see buildConsoleTabs): Abilities
        // (raw-layer fallback in abilityEntriesOfType, no compile needed), Castable Spells, Description,
        // GM. PCs and everything else still fall back to the server render.
        if (_blobType !== 'creature' && _blobType !== 'lair') return false;
        const ecId = String(encounterCreatureField(blob.encounter_creatures, 'encounter_creature_id') ?? '');
        if (!uuidOk(ecId)) {
            // STATIC / LIBRARY ITEM CREATURE (a creature statblock NOT placed in an encounter, e.g. opened
            // from a catalog / bestiary in the side panel). Render the unified card client-side from the
            // cached blob — no open_table_item_inc.php server round-trip. ftRenderStaticCreatureCardFromCache
            // self-gates (raw natural_weapon layer → server) and returns false on anything it can't satisfy,
            // so this stays a strict cache-first optimization over the unchanged PHP fallback.
            const rendered = ftRenderStaticCreatureCardFromCache(casterId, target, opts, blob);
            if (rendered) opts.afterPaint?.();
            return rendered;
        }
        const sideInfo = !!opts.sideInfo;
        const windowSuffix = String(opts.domSuffix ?? '').replace(/[^A-Za-z0-9_]/g, '').slice(0, 64);
        const suffix = sideInfo ? `_ec_${hexOf(ecId)}` : windowSuffix ? `_win_${windowSuffix}` : '';
        const cardCol = opts.cardColClasses ?? (sideInfo ? 'col-12 h-100' : 'col-lg-6 col-md-12 h-100');
        // Header chips need the encounter-root catalog folded in for realiased member rows
        // (see buildCardNamesMap). Still used by renderCreatureSummonsLists below.
        const names = buildCardNamesMap(blob);
        const viewerMainId = String(opts.viewerCasterId ?? w.ftGetMasterCasterId?.() ?? '');
        const access = creatureDisplayAccess(blob, viewerMainId, opts.viewerCanControl
            ?? (hasPlayerPermission(blob) || hasCampaignPermission(blob) || ftIsModifyEncounterToolsPage()));

        // Lair abilities: rebuild computed.creature.abilities with full defs BEFORE the ability pane
        // hydrates (ftMountPcSheet below reads it), so the console pane renders them like a creature.
        if (_blobType === 'lair') {
            try { lairPopulateAbilitiesFromCatalog(blob); } catch (e) { /* non-fatal */ }
        }

        // MEDIA — full render_carousel() parity (multi-picture/video, indicators/controls, the
        // permission-gated presentation-picture button), blob-first. Active pic = the EC thumbnail/presentation
        // pic (PHP order); set-pic buttons only for the controller (hasPlayerPermission).
        const ecBucket = recordOf(blob.encounter_creatures) ?? {};
        const presentationPicId = bucketScalar(ecBucket, 'encounter_creature_presentation_pic_id');
        const thumbPicId = bucketScalar(ecBucket, 'encounter_creature_thumbnail_pic_id');
        const carouselHtml = buildCarousel({
            domId: `${hexOf(ecId)}${suffix}`,
            itemName: bucketScalar(ecBucket, 'encounter_creature_name'),
            pics: pictureListOf(blob),
            videos: itemVideos(blob),
            activePicId: thumbPicId || presentationPicId || null,
            setPic: access.control
                ? { encounterCreatureId: ecId, presentationPicId, thumbnailPicId: thumbPicId }
                : null,
        });
        const mediaHtml = `
            <div>
                <div class="d-flex align-items-center justify-content-center">
                    ${carouselHtml}
                </div>
            </div>`;

        const lastUpdated = lastUpdatedOf(blob);

        const summaryTarget = `ft_creature_summary_${sideInfo ? 'si_' : ''}${ecId.replace(/[^A-Za-z0-9_]/g, '')}${suffix}`;

        const html = `
        <div class="card ${esc(cardCol)}" data-ft-pc-owns-sheet="1" data-ft-caster-id="${esc(ecId)}" data-ft-creature-card-cache="1"
            data-ft-viewer-can-control="${access.control ? '1' : '0'}" data-ft-viewer-caster-id="${esc(viewerMainId)}">
            ${mediaHtml}
            <div class="card-body text-left">
                <input type="hidden" class="ft-input-active-encounter-caster" id="input_active_caster_sheet${suffix}" value="${esc(ecId)}">
                <input type="hidden" id="input_last_updated${suffix}" value="${esc(lastUpdated ?? '')}">
                ${sideInfo ? `<input id="input_hidden_sidelog_encounter_creature" type="hidden" value="${esc(ecId)}">` : ''}
                ${paneMount('creature_header', ecId)}
                <hr />
                ${access.control ? paneMount('creature_summary', ecId, ` id="${summaryTarget}" data-ft-pc-side-info="${sideInfo ? '1' : '0'}"`) : ''}
                ${buildCreatureSocialActions(blob, ecId, viewerMainId)}
                ${buildConsoleTabs(blob, ecId, suffix, sideInfo, viewerMainId, access.control)}
            </div>
            ${sourceFooterHtml(blob)}
        </div>`;

        replaceHydratedHtml(target, html);
        delete target.dataset.ftCreatureCardRefused;
        mountedCreatureCards.set(target, { opts, access: accessKey(blob, viewerMainId, access.control) });

        // ── hydration (the PHP path runs these via inline bootstraps / loadPostFunctions) ─────────
        try { w.ftMountPcSheet(target); } catch (e) { try { w.ftMountPcSheet(); } catch (e2) { /* re-scan covers */ } }
        // Lair Abilities tab: render the ability/at-will-spell list-display (names resolved) into the
        // mount buildConsoleTabs emitted. renderItemListDisplay self-hydrates its own icons/runes.
        // The header, summary row (vitality / mana / AC bars) and Core Abilities banner are now
        // 'creature_header' / 'creature_summary' / 'creature_core_abilities' .ft-pc-sheet-panes, so
        // ftMountPcSheet above paints them on first render AND ftRerenderPcSheet repaints them on every
        // caster_update — keeping live combat state on the card current (heal-to-full, AP/mana spend,
        // bleed glow). The banner pane render re-hydrates the special-actions strip it hosts, so the
        // old explicit renderCasterSpecialActionsInline call here is no longer needed.
        try {
            const summonsEl = target.querySelector('[data-ft-creature-summons="1"]') as HTMLElement | null;
            if (summonsEl && typeof w.renderCreatureSummonsLists === 'function') {
                w.renderCreatureSummonsLists(blob, names, summonsEl);
            }
        } catch (e) { /* non-fatal */ }
        // (GM action strip is now a data-ft-pc-pane="creature_gm" mount — ftMountPcSheet above hydrates it,
        // same as every other tab. No explicit renderCreatureGmControlsInline call needed here.)
        try { w.ftMountAbilityViewToolbars?.(); } catch (e) { /* non-fatal */ }
        try { w.ftMountIcons?.(target); } catch (e) { /* footer pass covers */ }
        try { w.ftMountRuneIcons?.(target); } catch (e) { /* footer pass covers */ }
        // The Family pane is a catalog-list mount injected by innerHTML, so it lands AFTER the
        // bundle's own load + DOMContentLoaded scans have already run. ftMountCatalogLists() guards
        // against double-mounting, so re-invoking it is a no-op for anything already hydrated.
        try { w.ftMountCatalogLists?.(); } catch (e) { /* non-fatal */ }
        try { w.ftMountThumbnails?.(target); } catch (e) { /* footer pass covers */ }
        try { w.ftMountParsedText?.(target); } catch (e) { /* footer pass covers */ }
        try { w.ftMountParseTextTables?.(target); } catch (e) { /* footer pass covers */ }
        try { w.ftMountInventoryTiles?.(target); } catch (e) { /* footer pass covers */ }
        try { w.refreshDragAndDrop?.(); } catch (e) { /* non-fatal */ }

        // Side-info registry: remember what's open (creature panes self-repaint; the registry's
        // object-card re-render path no-ops for creatures, so this only feeds open/clear tracking).
        if (sideInfo) {
            try { setOpenSideInfo(ecId, target.id); } catch (e) { /* non-fatal */ }
        }

        restoreFt3dCreatureSheetView(target, ecId);

        ftDebugLog('caster', { creatureCardCacheRender: ecId, sideInfo });
        opts.afterPaint?.();
        return true;
    } catch (e) {
        console.warn('ftRenderCreatureCardFromCache failed', e);
        return false;
    }
}

// ── STATIC / LIBRARY ITEM-CREATURE CARD (client-side port of table_creature_inc.php's library branch) ──
// A creature statblock viewed outside any encounter (item.php / catalog / bestiary side-info). The PHP
// path forces has_encounter_creature_permission=true (a library statblock is public) and renders a
// reference-only console: no GM tab, no status-effects tab, no movement tab, no Core Abilities banner.
// Every tab's CONTENT already renders client-side (the same .ft-pc-sheet-pane renderers honoring
// data-ft-static-creature, plus the list-display window renderers); this builds the SHELL + pills from
// the cached blob so the whole card needs zero server round-trip.

// HEADER (port of _creature_header_inc.php, LIBRARY branch). Inline (not the creature_header PANE,
// which requires an encounter id) — a static statblock's header carries no live coords/PL state.
function buildStaticHeader(blob: CreatureBlob, itemId: string, names: ReferencedNames): string {
    const nameOf = (uuid: string): string => {
        const n = names[String(uuid).toUpperCase()];
        const nm = n && typeof n === 'object' ? (n.item_name ?? n.name) : n;
        return typeof nm === 'string' ? nm : '';
    };

    let h = '<div class="card-title"><div class="d-flex align-items-center">';
    h += '<div class="d-flex align-items-center justify content start flex-grow-1 h5 mb-0 pb-0">';
    h += `<div>${esc(String(itemsCol(blob, 'item_name') ?? ''))}</div>`;
    if (Number(itemsCol(blob, 'item_private') ?? 0)) {
        h += `<div><span class="ml-2 badge bg-secondary">${iconPh('encounter_creature_private')} Private</span></div>`;
    }
    h += '</div>';

    h += '<div><h5>';
    const heroforge = layerOf(blob, 'heroforge_link');
    if (heroforge) h += `<a href="${esc(String(heroforge))}" target="_blank">${iconPh('heroforge_link')}</a> `;
    const gender = layerOf(blob, 'gender');
    if (gender) h += `<span role="button" ${ccInfoAttrs(String(gender))}>${iconPh(String(gender), 20, 20)}</span> `;
    if (layerHas(blob, 'creature_type')) {
        const ctId = String(layerOf(blob, 'creature_type') ?? '');
        if (ctId) h += `<a href="${BASE_URL}item.php?id=${esc(ctId)}">${iconPh(ctId)}</a>`;
    }
    h += '</h5></div></div>';

    // OBJECT TAGS row
    h += '<div class="d-flex align-items-center">';
    h += '<div class="d-flex align-items-center flex-grow-1 flex-wrap gap-2 min-w-0">';
    // sizeIdOf bridges to computed.creature.size.breakdown.final_size_id first (mirrors PHP
    // layer_value('size')) — a bare layerOf read can surface a taxonomy carrier's BASE size
    // now that PC blobs retain the creature_sub_type carrier slot.
    const sizeRaw = sizeIdOf(blob) ?? layerOf(blob, 'size');
    if (sizeRaw) {
        const sizeId = String(sizeRaw);
        // Library: the size badge opens the size item's own info popup (no encounter size breakdown).
        h += `<div class="flex-shrink-0"><span role="button" ${ccInfoAttrs(sizeId)}>${iconPh(sizeId)}</span></div>`;
    }
    for (const layerKey of ['creature_sub_type', 'creature_template'] as const) {
        if (!layerHas(blob, layerKey)) continue;
        const badge = layerKey === 'creature_sub_type' ? 'bg-warning' : 'bg-secondary';
        for (const entry of layerEntriesOf(blob, layerKey)) {
            const uuid = String(layerListEntryUuid(entry) ?? scalarLayerSlot(entry) ?? '');
            if (!uuidOk(uuid)) continue;
            const nested = nestedItemName(entry);
            const label = (typeof nested === 'string' && nested !== '') ? nested : nameOf(uuid);
            if (!label) continue;
            h += `<div class="flex-shrink-0"><span class="badge ${badge}" role="button" ${ccInfoAttrs(uuid)}>${esc(label)}</span></div>`;
        }
    }
    h += '</div>';

    h += '<div class="d-flex align-items-center">';
    const costGp = layerOf(blob, 'cost_gp');
    if (costGp !== null && costGp !== undefined) {
        const n = Number(costGp) || 0;
        h += `<div class="pr-1"><span class="d-inline-block" data-bs-toggle="tooltip" title="Cost GP" data-bs-original-title="Cost GP"><span class="badge bg-warning text-dark">${n === 0 ? 'Priceless' : `${prettyNum(n)} gp`}</span></span></div>`;
    }
    if (layerHas(blob, 'daily_cost_gp')) {
        const n = Number(layerOf(blob, 'daily_cost_gp') ?? 0) || 0;
        h += `<div class="pr-1"><span class="d-inline-block" data-bs-toggle="tooltip" title="Daily Cost GP" data-bs-original-title="Daily Cost GP"><span class="badge bg-warning text-dark">${prettyNum(n)} gp / day</span></span></div>`;
    }
    if (layerHas(blob, 'weight')) {
        const n = Number(layerOf(blob, 'weight') ?? 0) || 0;
        h += `<div class="pr-1"><span class="badge bg-primary">${prettyNum(n)} lb${n > 1 ? 's' : ''}</span></div>`;
    }
    if (layerHas(blob, 'legendary_creature')) {
        h += `<div class="pr-1"><span role="button" ${ccInfoAttrs(INFO_LEGENDARY)}>${iconPh('legendary_creature')}</span></div>`;
    }
    if (layerHas(blob, 'merchant')) h += `<div class="mr-1">${iconPh('merchant')}</div>`;
    if (layerOf(blob, 'trainer')) h += `<div class="mr-1">${iconPh('trainer')}</div>`;
    if (layerHas(blob, 'creature_role')) {
        const roleId = String(layerOf(blob, 'creature_role') ?? '');
        if (roleId) h += `<div class="pr-1"><span role="button" ${ccInfoAttrs(roleId)}>${iconPh(roleId)}</span></div>`;
    }
    if (layerHas(blob, 'default_team')) {
        const teamId = String(layerOf(blob, 'default_team') ?? '');
        if (teamId) h += `<div class="pr-1"><span role="button" ${ccInfoAttrs(teamId)}>${iconPh(teamId)}</span></div>`;
    }
    const crRaw = layerOf(blob, 'cr');
    if (crRaw !== null && crRaw !== undefined) {
        const cr = Number(crRaw) || 0;
        const crLabel = cr > 0 && cr < 1 ? fracOf(cr) : String(cr);
        h += `<div class="pr-1"><span class="d-inline-block" role="button" data-bs-toggle="tooltip" data-bs-original-title="Challenge Rating" ${ccInfoAttrs(INFO_CR)}><span class="badge bg-danger">${esc(crLabel)}</span></span></div>`;
    }
    const plTotal = powerLevelOf(blob);
    if (plTotal !== undefined && plTotal !== null) {
        h += `<div class="pr-1"><span class="d-inline-block" role="button" data-bs-toggle="tooltip" data-bs-original-title="Power Level: ${esc(plExact(plTotal))}" ${ccPowerAttrs(itemId)}><span class="badge bg-white text-dark">${esc(plPretty(plTotal))}</span></span></div>`;
    }
    const meshMarker = renderMeshMarker(blob, names);
    if (meshMarker) h += `<div class="pr-1">${meshMarker}</div>`;
    h += '</div></div></div>';
    return h;
}

// ABILITIES TAB (static): class-mechanic bars + the read-only view toggle bar (NO Core Abilities banner)
// + search + the static ability_sections pane + the summons/lair/innate mount. Twin of _abilities_inc.php
// with $ft_static_item_creature = true.
function buildStaticAbilitiesTab(blob: CreatureBlob, itemId: string, sideInfo: boolean): string {
    const rootId = `ft_sheet_abilities_${itemId.replace(/[^A-Za-z0-9_-]/g, '_')}`;
    const searchId = `${rootId}_search`;
    let summonsMount = '';
    if (
        layerHas(blob, 'shapeshift') || layerHas(blob, 'summon') || layerHas(blob, 'summon_type')
        || layerHas(blob, 'cast_spell_through_summon_range') || layerHas(blob, 'at_will_spell')
        || layerHas(blob, 'innate_spell_per_long_rest')
        || layerHas(blob, 'lair') || layerHas(blob, 'regional_effect')
    ) {
        summonsMount = `<div id="ft_creature_summons_${hexOf(itemId)}" data-ft-creature-summons="1"></div>`;
    }
    return `${buildClassMechanicBars(blob, sideInfo, false)}${ABILITIES_TAB_STYLE}
    <div class="fate-tide-sheet-abilities-search-root${sideInfo ? ' ft-abilities--sideinfo-host' : ''}" id="${rootId}">
        <div class="ft-abilities-header-wrap">
            <div class="ft-ability-view-toolbar-bar d-flex align-items-center mb-2"><span class="ft-ability-view-toolbar-host"></span></div>
            <div class="row g-2 align-items-center my-3 w-100 mx-0 ft-abilities-search-row">
                <div class="col-12 col-md-auto ms-md-auto ft-abilities-search-anchor">
                    <label class="visually-hidden" for="${searchId}">Search abilities</label>
                    <div class="position-relative">
                        <input type="search" id="${searchId}" class="form-control form-control-sm fate-tide-sheet-ability-search pe-4"
                            data-ft-sheet-abilities-root="${rootId}" placeholder="Search abilities…" autocomplete="off">
                        <svg class="svg-icon position-absolute top-50 end-0 translate-middle-y me-2 ft-sheet-search-icon" aria-hidden="true"><use href="#search"></use></svg>
                    </div>
                </div>
            </div>
        </div>
        ${paneMount('ability_sections', itemId, ' data-ft-static-creature="1"')}
        ${summonsMount}
    </div>`;
}

// WEAPONS TAB (static): NO live core_attacks pane; natural-weapons + equipped-weapons panes (static) +
// the attack/opportunity-attack abilities pane + search. Twin of _weapons_equipped_inc.php with
// $ft_static_item_creature = true. (Entry already routed a RAW natural_weapon layer to the server.)
function buildStaticWeaponsTab(blob: CreatureBlob, itemId: string, sideInfo: boolean): string {
    const rootId = `ft_sheet_core_attacks_${itemId.replace(/[^A-Za-z0-9_-]/g, '_')}`;
    const searchId = `${rootId}_search`;
    let attacksBlock = '';
    if (layerHas(blob, 'attack_ability') || layerHas(blob, 'opportunity_attack_ability')) {
        attacksBlock = `
        <div class="ft-attacks-banner-wrap">
            <div class="row g-2 align-items-center my-3 w-100 mx-0 ft-attacks-search-row">
                <div class="col-12 col-md-auto ms-md-auto ft-attacks-search-anchor">
                    <label class="visually-hidden" for="${searchId}">Search attack abilities</label>
                    <div class="position-relative">
                        <input type="search" id="${searchId}" class="form-control form-control-sm fate-tide-sheet-ability-search pe-4"
                            data-ft-sheet-abilities-root="${rootId}" placeholder="Search attacks…" autocomplete="off">
                        <svg class="svg-icon position-absolute top-50 end-0 translate-middle-y me-2 ft-sheet-search-icon" aria-hidden="true"><use href="#search"></use></svg>
                    </div>
                </div>
            </div>
            ${paneMount('weapon_abilities', itemId, ' data-ft-static-creature="1"')}
        </div>`;
    }
    return `${WEAPONS_TAB_STYLE}
    <div class="fate-tide-sheet-abilities-search-root${sideInfo ? ' ft-weapons-equipped--sideinfo-host' : ''}" id="${rootId}">
        ${paneMount('natural_weapons', itemId, ' data-ft-static-creature="1"')}
        ${paneMount('equipped_weapons', itemId, ' data-ft-static-creature="1"')}
        ${attacksBlock}
    </div>`;
}

// CONSOLE TABS (static subset). Mirrors _creature_console_tabs_inc.php's visibility for a library
// statblock; note the static flag is applied ONLY to the panes the PHP flags (ability_sections /
// passive / natural+equipped weapons / weapon_abilities) — skills / castable / mentas / defenses read
// their computed clusters the same way for static and live creatures, so they carry no static flag.
// The static Inventory tab renders the equipment paper-doll + the equipped/unequipped lists. For a
// library/reference item creature (no add/equip UI) it's worth a pill ONLY when at least one of those
// has content: equipped OR unequipped inventory, OR intrinsic natural armor / natural weapons that the
// paper-doll now surfaces (a barehanded creature's "gear" is its body). Otherwise the tab renders blank
// — bad UX (owner 2026-07-03) — so we drop the pill + pane entirely. Mirror of the PHP
// creature_inventory_tab_has_content() gate.
export function staticInventoryTabHasContent(blob: CreatureBlob): boolean {
    if (layerHas(blob, 'inventory_equipped')) return true;
    if (layerHas(blob, 'inventory_unequipped')) return true;
    if (layerHas(blob, 'natural_armor')) return true;
    // Natural weapons only fill a hand cell when their FULL def is in the blob — the equipment_slot lives
    // on the weapon_classification carrier, so a name-only stub can't be placed. On a static item page the
    // natural weapon is LEANED to a {item_name,item_type} catalog stub (the Weapons tab resolves it
    // server-side via query_full_item_arr), so the paper-doll renders blank there — showing the tab would
    // just reproduce the blank pane the owner flagged. Count a natural weapon only when a full def
    // (item_layer_types present) is actually in hand; on PC / live-creature blobs it is, so the tab shows.
    const catalog = recordOf(blob.catalog);
    const isFullDef = (value: unknown): boolean => {
        const itemLayerTypes = recordOf(recordOf(value)?.item_layer_types);
        return !!itemLayerTypes && Object.keys(itemLayerTypes).length > 0;
    };
    const catLookup = (id: string): CreatureBlob | null => {
        if (!catalog || id === '') return null;
        return recordOf(catalog[id] ?? catalog[id.toUpperCase()] ?? catalog[id.toLowerCase()]);
    };
    const nwEntries: unknown[] = [];
    const pristine = layerEntriesOf(blob, 'natural_weapon');
    if (Array.isArray(pristine)) nwEntries.push(...pristine);
    const derived = computedCreature(blob)?.natural_weapon;
    if (Array.isArray(derived)) nwEntries.push(...derived);
    for (const e of nwEntries) {
        if (isFullDef(e)) return true;
        const entry = recordOf(e);
        const uuid = typeof e === 'string' ? e : String(itemsCol(entry, 'item_id') ?? entry?.item_id ?? '');
        if (isFullDef(catLookup(uuid))) return true;
    }
    return false;
}

// Static/library creatures do not receive the encounter-only Core Abilities. Hide their Abilities
// tab when neither the shared ability_sections pane nor the creature summons/spells mount can paint
// anything. Live encounter creatures deliberately keep the tab (buildConsoleTabs) for Core Abilities.
const STATIC_CREATURE_ABILITIES_TAB_LAYERS = [
    'lair_ability',
    'full_ability',
    'ability',
    'quick_ability',
    'free_ability',
    'reaction_ability',
    'curse',
    'sanguine',
    'ability_choice',
    'charged_ability',
    'shapeshift',
    'summon',
    'summon_type',
    'cast_spell_through_summon_range',
    'at_will_spell',
    'innate_spell_per_long_rest',
] as const;

export function staticAbilitiesTabHasContent(blob: CreatureBlob): boolean {
    const creature = computedCreature(blob);
    const compiled = recordOf(creature?.abilities);
    const skillAbilities = recordOf(creature?.skill_ability);
    const bucketHasEntries = (value: unknown): boolean => {
        if (Array.isArray(value)) return value.length > 0;
        const record = recordOf(value);
        if (record) return Object.keys(record).length > 0;
        return value !== null && value !== undefined && value !== '' && value !== false;
    };

    for (const layer of STATIC_CREATURE_ABILITIES_TAB_LAYERS) {
        if (bucketHasEntries(compiled?.[layer]) || bucketHasEntries(skillAbilities?.[layer])) return true;
        // Match the ability pane's actual read funnel. The shared module carrier walker can resolve
        // catalog-provider rows that the pane's ftLayerEntries reader does not render, producing a
        // visible-but-empty tab (Ghast: its Undead taxonomy carrier triggered this mismatch).
        if (typeof ftLayerEntries === 'function' && ftLayerEntries(blob, layer).length > 0) return true;
    }
    return false;
}

// ── FAMILY TAB (creature family trees, 2026-08-20) ───────────────────────────────────────────────
// Whether to show the Family pill, and the pane that hosts it.
//
// ⚠ The gate CANNOT be computed from the blob. Kin edges live in the `kin_links` table, not in item
// layers — that was the whole design ruling — so nothing about a creature's genealogy is in
// $item_arr. The server stamps `_ft_kin_tab` onto the blob at MOUNT time
// (ft_caster_card_render_mount), which checks whether a family exists. Reaching for a
// layer here would silently hide the tab for every creature in the game.
//
// A missing flag means the mount predates this feature (or the stamp threw): hide the tab rather
// than show an empty one, which is the same rule the Inventory tab follows (owner 2026-07-03).
function kinTabVisible(blob: CreatureBlob): boolean {
    return Number((blob as unknown as Record<string, unknown>)._ft_kin_tab ?? 0) === 1;
}

function kinTabKind(blob: CreatureBlob): string {
    const kind = String((blob as unknown as Record<string, unknown>)._ft_kin_kind ?? 'item');
    return kind === 'encounter_creature' ? 'encounter_creature' : 'item';
}

// The pane is a catalog-list mount, NOT a bespoke renderer. Creature cards are display surfaces, so
// this mount is explicitly read-only; editor routes may use the same renderer without that marker.
function buildKinFamilyPane(blob: CreatureBlob, id: string): string {
    return `<div data-ft-catalog-list data-ft-render="kin-family"
        data-ft-endpoint="options/api/kin_inc.php"
        data-ft-kin-kind="${esc(kinTabKind(blob))}"
        data-ft-kin-id="${esc(id)}"
        data-ft-kin-readonly="1"
        data-ft-empty-text="No family recorded for this creature yet."
        class="ft-kin-block p-2">
        <div data-ft-catalog-results></div>
        <div data-ft-catalog-status class="text-center text-muted py-3" style="display:none;"></div>
    </div>`;
}

function buildStaticConsoleTabs(blob: CreatureBlob, itemId: string, suffix: string, sideInfo: boolean): string {
    const spellsPrepared = recordOf(computedCreature(blob)?.spells_prepared);
    const hasSpellsPrepared = !!spellsPrepared && Object.keys(spellsPrepared).length !== 0;

    const tabs: Array<{ key: string; show: boolean }> = [
        { key: 'creature_description', show: true },
        { key: 'creature_skills', show: true },
        { key: 'creature_abilities', show: staticAbilitiesTabHasContent(blob) },
        // Dedicated Lair tab — static item-creature card ONLY (mirrors _creature_console_tabs_inc.php's
        // `!empty($ft_static_item_creature) && (layer_has(lair)||layer_has(regional_effect))` gate). The
        // live-EC branch omits it (the lair auto-spawns there as its own actor). Regressed when the card
        // moved to client render; this restores it.
        { key: 'creature_lair', show: layerHas(blob, 'lair') || layerHas(blob, 'regional_effect') },
        // Dedicated Mount tab — static item-creature card ONLY (mirrors _creature_console_tabs_inc.php's
        // `!empty($ft_static_item_creature) && layer_has(mount_creature)` gate). A creature carrying a
        // mount_creature layer is cavalry: it auto-spawns riding the referenced mount when added to an
        // encounter. The live-EC branch omits it (there the mount spawns as its own actor). Twin of Lair.
        { key: 'creature_mount', show: layerHas(blob, 'mount_creature') },
        { key: 'creature_weapons_equipped', show: true },
        { key: 'creature_passive_abilities', show: layerHas(blob, 'passive_ability') },
        {
            key: 'creature_castable_spells',
            show: layerHas(blob, 'spell_prepared_1') || layerHas(blob, 'spell_prepared_2')
                || layerHas(blob, 'spell_prepared_3') || layerHas(blob, 'at_will_spell')
                || layerHas(blob, 'innate_spell_per_long_rest') || hasSpellsPrepared,
        },
        { key: 'creature_mentas', show: hasMentaBucket(blob) },
        { key: 'creature_inventory', show: staticInventoryTabHasContent(blob) },
        { key: 'creature_skinning', show: true },
        { key: 'creature_defenses', show: true },
        { key: 'creature_family', show: kinTabVisible(blob) },
    ];
    const visible = tabs.filter((t) => t.show).map((t) => t.key);

    let active = 'creature_abilities';
    const rawCookie = cookieVal('cookie_player_character_sheet_menu_tab');
    if (rawCookie) {
        const sfx = rawCookie.replace(/^tab_/, '');
        if (sfx.startsWith('creature_')) active = sfx;
        else if (PC_TAB_SUFFIX_TO_CREATURE[sfx]) active = PC_TAB_SUFFIX_TO_CREATURE[sfx]!;
    }
    if (!visible.includes(active)) {
        active = visible.includes('creature_abilities') ? 'creature_abilities' : 'creature_description';
    }

    const colsPerRow = visible.length <= 2
        ? Math.max(1, visible.length)
        : Math.max(1, Math.ceil(visible.length / 2));
    let pills = '';
    for (const key of visible) {
        // 'creature_family' → its own `family_tree` glyph. ⚠ NOT 'ancestry', which belongs to the
        // ancestry item type. Mirrors the same alias in _creature_console_tabs_inc.php.
        const icon = key === 'creature_family' ? 'family_tree' : key.replace(/^creature_/, '');
        const isActive = key === active;
        pills += `<li class="nav-item p-1" role="presentation">
            <button id="tab_${key}${suffix}" class="nav-link border ${isActive ? 'active' : ''}"
                data-bs-target="#div_${key}${suffix}" aria-controls="div_${key}${suffix}"
                data-bs-toggle="pill" type="button" role="tab" ${isActive ? "aria-selected='true'" : ''}
                data-ft-creature-card-action="set-tab-cookie"
                data-ft-tab="${key}"
            >${iconPh(icon)}</button>
        </li>`;
    }

    const pane = (key: string, inner: string, extraClass = ''): string => `
        <div id="div_${key}${suffix}" class="tab-pane fade ${key === active ? 'show active' : ''}${extraClass}"
            aria-labelledby="tab_${key}${suffix}" role="tabpanel">${inner}</div>`;

    let panes = '';
    panes += pane('creature_description', buildDescriptionTab(blob, itemId, true));
    panes += pane('creature_skills', paneMount('skills', itemId));
    if (visible.includes('creature_abilities')) {
        panes += pane('creature_abilities', buildStaticAbilitiesTab(blob, itemId, sideInfo));
    }
    if (visible.includes('creature_lair')) {
        // Hydrated post-injection via window.renderCreatureLairLists(blob, names, el) — the same
        // renderer the PHP ft_ld_ts_creature_lair_mount island feeds (lair + regional-effect blocks).
        panes += pane('creature_lair', `<div id="ft_creature_lair_static_${hexOf(itemId)}" data-ft-creature-lair="1"></div>`);
    }
    if (visible.includes('creature_mount')) {
        // Hydrated post-injection via window.renderCreatureMountLists(blob, names, el) — the same
        // renderer the PHP ft_ld_ts_creature_mount_mount island feeds (the mount_creature block).
        panes += pane('creature_mount', `<div id="ft_creature_mount_static_${hexOf(itemId)}" data-ft-creature-mount="1"></div>`);
    }
    panes += pane('creature_weapons_equipped', buildStaticWeaponsTab(blob, itemId, sideInfo));
    if (visible.includes('creature_passive_abilities')) {
        panes += pane('creature_passive_abilities', buildPassiveAbilitiesSearchWrap(itemId, paneMount('passive_abilities', itemId, ' data-ft-static-creature="1"')));
    }
    if (visible.includes('creature_castable_spells')) {
        panes += pane('creature_castable_spells', paneMount('castable_spells', itemId));
    }
    if (visible.includes('creature_mentas')) {
        panes += pane('creature_mentas', paneMount('mentas', itemId));
    }
    // Inventory + Skinning: list-display window renderers (hydrated after innerHTML, below).
    if (visible.includes('creature_inventory')) {
        panes += pane('creature_inventory', `<div id="ft_creature_inventory_static_${hexOf(itemId)}" data-ft-creature-inventory-static="1"></div>`);
    }
    panes += pane('creature_skinning', `<div class="row px-3"><div class="col-12"><h5 class="mb-0 pt-2 pb-2 bg-secondary text-dark bg-gradient rounded-top text-center">Skinning</h5><div id="ft_creature_skinning_static_${hexOf(itemId)}" data-ft-creature-harvest="1"></div></div></div>`);
    panes += pane('creature_defenses', paneMount('defenses', itemId), ' text-left');
    if (visible.includes('creature_family')) {
        panes += pane('creature_family', buildKinFamilyPane(blob, itemId), ' text-left');
    }

    return `
    <div${sideInfo ? ' class="mt-2"' : ''}>
        <ul class="nav nav-pills pt-1 pb-1 border border-bottom-0 rounded-top character-console-nav${sideInfo ? ' character-console-nav--sideinfo' : ''}"
            id="nav_tabs_character_console${suffix}" role="tablist"
            ${sideInfo ? `style="--ft-console-cols: ${colsPerRow};"` : ''}>${pills}</ul>
        <div class="tab-content border overflow-auto pl-1 pr-1" style="height: 595px; scrollbar-width: thin;">${panes}</div>
    </div>`;
}

function ftRenderStaticCreatureCardFromCache(
    itemId: string,
    target: HTMLElement,
    opts: CreatureCardOpts,
    blob: CreatureBlob,
): boolean {
    try {
        const w = creatureWindow();
        if (typeof w.ftMountPcSheet !== 'function') return false;
        // Raw and computed natural weapons share the player-sheet weapon-table renderer; no PHP
        // per-weapon table is required on the static route.

        const sideInfo = !!opts.sideInfo;
        const windowSuffix = String(opts.domSuffix ?? '').replace(/[^A-Za-z0-9_]/g, '').slice(0, 64);
        const suffix = sideInfo ? `_ec_${hexOf(itemId)}` : windowSuffix ? `_win_${windowSuffix}` : '';
        const cardCol = opts.cardColClasses ?? (sideInfo ? 'col-12 h-100' : 'col-lg-6 col-md-12 h-100');
        const names = buildCardNamesMap(blob);

        const carouselHtml = buildCarousel({
            domId: `${hexOf(itemId)}${suffix}`,
            itemName: String(itemsCol(blob, 'item_name') ?? ''),
            pics: pictureListOf(blob),
            videos: itemVideos(blob),
            activePicId: null,
            setPic: null,
        });
        const mediaHtml = `
            <div>
                <div class="d-flex align-items-center justify-content-center">${carouselHtml}</div>
            </div>`;

        const lastUpdated = lastUpdatedOf(blob);
        const summaryTarget = `ft_creature_summary_${sideInfo ? 'si_' : ''}${itemId.replace(/[^A-Za-z0-9_]/g, '')}`;

        const html = `
        <div class="card ${esc(cardCol)}" data-ft-caster-id="${esc(itemId)}" data-ft-creature-card-cache="1" data-ft-static-creature="1">
            ${mediaHtml}
            <div class="card-body text-left">
                <input type="hidden" id="input_last_updated${suffix}" value="${esc(lastUpdated ?? '')}">
                ${buildStaticHeader(blob, itemId, names)}
                <hr />
                ${paneMount('creature_summary', itemId, ` id="${summaryTarget}" data-ft-pc-side-info="${sideInfo ? '1' : '0'}"`)}
                ${buildStaticConsoleTabs(blob, itemId, suffix, sideInfo)}
            </div>
            ${sourceFooterHtml(blob)}
        </div>`;

        replaceHydratedHtml(target, html);

        try { w.ftMountPcSheet(target); } catch (e) { try { w.ftMountPcSheet(); } catch (e2) { /* re-scan covers */ } }
        // Inventory / skinning-yields / summons → list-display window renderers, from the cached blob.
        try {
            const summonsEl = target.querySelector('[data-ft-creature-summons="1"]') as HTMLElement | null;
            if (summonsEl && typeof w.renderCreatureSummonsLists === 'function') w.renderCreatureSummonsLists(blob, names, summonsEl);
        } catch (e) { /* non-fatal */ }
        try {
            const lairEl = target.querySelector('[data-ft-creature-lair="1"]') as HTMLElement | null;
            if (lairEl && typeof w.renderCreatureLairLists === 'function') w.renderCreatureLairLists(blob, names, lairEl);
        } catch (e) { /* non-fatal */ }
        try {
            const mountEl = target.querySelector('[data-ft-creature-mount="1"]') as HTMLElement | null;
            if (mountEl && typeof w.renderCreatureMountLists === 'function') w.renderCreatureMountLists(blob, names, mountEl);
        } catch (e) { /* non-fatal */ }
        try {
            const invEl = target.querySelector('[data-ft-creature-inventory-static="1"]') as HTMLElement | null;
            if (invEl && typeof w.renderCreatureInventory === 'function') w.renderCreatureInventory(blob, names, invEl);
        } catch (e) { /* non-fatal */ }
        try {
            const harvEl = target.querySelector('[data-ft-creature-harvest="1"]') as HTMLElement | null;
            if (harvEl && typeof w.renderCreatureHarvestYields === 'function') w.renderCreatureHarvestYields(blob, names, harvEl);
        } catch (e) { /* non-fatal */ }
        try { w.ftMountAbilityViewToolbars?.(); } catch (e) { /* non-fatal */ }
        try { w.ftMountIcons?.(target); } catch (e) { /* footer pass covers */ }
        try { w.ftMountRuneIcons?.(target); } catch (e) { /* footer pass covers */ }
        // The Family pane is a catalog-list mount injected by innerHTML, so it lands AFTER the
        // bundle's own load + DOMContentLoaded scans have already run. ftMountCatalogLists() guards
        // against double-mounting, so re-invoking it is a no-op for anything already hydrated.
        try { w.ftMountCatalogLists?.(); } catch (e) { /* non-fatal */ }
        try { w.ftMountThumbnails?.(target); } catch (e) { /* footer pass covers */ }
        try { w.ftMountCarousels?.(target); } catch (e) { /* footer pass covers */ }
        try { w.ftMountParsedText?.(target); } catch (e) { /* footer pass covers */ }
        try { w.ftMountParseTextTables?.(target); } catch (e) { /* footer pass covers */ }
        try { w.ftMountInventoryTiles?.(target); } catch (e) { /* footer pass covers */ }
        try { w.refreshDragAndDrop?.(); } catch (e) { /* non-fatal */ }

        if (sideInfo) { try { setOpenSideInfo(itemId, target.id); } catch (e) { /* non-fatal */ } }
        ftDebugLog('caster', { staticCreatureCardCacheRender: itemId, sideInfo });
        return true;
    } catch (e) {
        console.warn('ftRenderStaticCreatureCardFromCache failed', e);
        return false;
    }
}

Object.assign(window, {
    ftRefreshCreatureCardAccess,
    ftRenderCreatureCardFromCache,
    ftRenderCreatureHeaderFromCache,
    ftRenderCreatureCoreAbilitiesFromCache,
});

export { ftRenderCreatureCardFromCache, ftRenderCreatureHeaderFromCache, ftRenderCreatureCoreAbilitiesFromCache };
